import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { claude } from '../claude';

/* ============================================================
   CONFIG CENTRALIZZATA
   Nessun valore di bilanciamento hardcoded nel corpo del componente.
   ============================================================ */
const CONFIG = {
  colori: {
    trasporto: '#EB1C24',
    alloggio: '#0071BA',
    attivita: '#FF5B00',
    servizi: '#009045',
    dark: '#0a1628',
    chiaro: '#f5f7fa',
    bordo: '#dfe5ec',
    testoSoft: '#6b7a8d',
  },
  punti: {
    guidata: 100,
    raccontoLibero: 40,
    bonusSegmentoCompilato: 15,
  },
  gate: {
    cardAlGiornoSenzaEsperienze: 3,
    giorniSbloccoDopoRacconto: 30,
  },
  mezzi: [
    { id: 'aereo', label: 'Aereo', icona: '✈️' },
    { id: 'treno', label: 'Treno', icona: '🚆' },
    { id: 'auto', label: 'Auto', icona: '🚗' },
    { id: 'moto', label: 'Moto', icona: '🏍️' },
    { id: 'bicicletta', label: 'Bicicletta', icona: '🚲' },
    { id: 'pubblici', label: 'Bus / Flixbus', icona: '🚌' },
  ],
  mezziSuStrada: ['auto', 'moto', 'bicicletta'],
  fasceTariffa: [
    'Meno di €50', '€50 – €100', '€100 – €150', '€150 – €200', '€200 – €250',
    '€250 – €300', '€300 – €350', '€350 – €400', '€400 – €450', '€450 – €500',
    'Più di €500',
  ],
  classiVolo: ['Economy Class', 'Premium Economy Class', 'Business Class', 'First Class (Prima Classe)'],
  compagnieTreno: [
    { id: 'italo', label: 'Italo Treno' },
    { id: 'trenitalia', label: 'Trenitalia (Frecciarossa)' },
  ],
  classiTreno: {
    italo: ['Smart', 'Prima', 'Club Executive'],
    trenitalia: ['Standard', 'Premium', 'Business', 'Executive'],
  },
  tipiAlloggio: [
    { id: 'bnb', label: 'B&B', icona: '/images/alloggio/icone/B&B.svg' },
    { id: 'divano', label: 'Divano', icona: '/images/alloggio/icone/COUCHSURFING.svg' },
    { id: 'casa', label: 'Casa', icona: '/images/alloggio/icone/APPARTAMENTO.svg' },
    { id: 'ostello', label: 'Ostello', icona: '/images/alloggio/icone/OSTELLO.svg' },
    { id: 'hotel', label: 'Hotel', icona: '/images/alloggio/icone/HOTEL.svg' },
    { id: 'campeggio', label: 'Campeggio', icona: '/images/alloggio/icone/TENDA.svg' },
  ],
  // ⚠️ Colonne filtri "Seleziona i criteri che hai utilizzato" (alloggio_5.jpg)
  criteriAlloggio: {
    posizione: [
      'Posizione centrale', 'Posizione isolata', 'Posizione isolata, vicina ai mezzi',
      'Preferita dagli ospiti', 'Zone migliori fuori dal centro', 'Scelti da OCTA',
    ],
    recensioni: ['1 stella', '2 stelle', '3 stelle', '4 stelle', '5 stelle'],
    comfort: [
      'Bagno privato', 'Aria condizionata', 'Balcone', 'Cucina/angolo cottura', 'Vasca',
      'Vista panoramica', 'Vasca idromassaggio', 'TV schermo piatto', 'Lavastoviglie',
      'Camino', 'Biancheria da letto', 'Spa', 'Palestra', 'Spa & centro benessere',
      'Stazione ricarica veicoli elettrici',
    ],
    tipologia: [
      'Case e interi appartamenti', 'Appartamenti', 'Hotel', 'Affitta camere', 'Ostelli',
      'B&B', 'Hotel a capsule', 'Campeggio', 'Ville', 'Case vacanze', 'Chalet',
    ],
    servizi: [
      'Pensione intera', 'Mezza pensione', 'Solo colazione', 'Noleggio bici', 'Noleggio auto',
      'Cancellazione gratuita', 'Connessione WiFi gratuita', 'Parcheggio coperto',
      'Per celiaci', 'Accesso animali', 'Accesso disabili',
    ],
  },
  tipiAttivita: [
    { id: 'museo', label: 'Museo / Visita' },
    { id: 'tour', label: 'Tour guidato' },
    { id: 'escursione', label: 'Escursione' },
    { id: 'gastronomia', label: 'Gastronomia' },
    { id: 'corso', label: 'Corso' },
    { id: 'serata', label: 'Vita notturna' },
    { id: 'sport', label: 'Sport' },
    { id: 'bambini', label: 'Attività per bambini' },
    { id: 'cinema', label: 'Cinema / Teatro' },
    { id: 'locali', label: 'Attività locali' },
  ],
  tipiServizio: [
    { id: 'noleggio_auto', label: 'Noleggio auto', copreAlloggio: false },
    { id: 'noleggio_camper', label: 'Noleggio camper / van', copreAlloggio: true },
    { id: 'noleggio_barca', label: 'Noleggio barca', copreAlloggio: true },
    { id: 'noleggio_moto', label: 'Noleggio moto / scooter', copreAlloggio: false },
    { id: 'assicurazione', label: 'Assicurazione viaggio', copreAlloggio: false },
    { id: 'parcheggio', label: 'Parcheggio', copreAlloggio: false },
    { id: 'altro', label: 'Altro', copreAlloggio: false },
  ],
  // Lista estendibile: consumo in litri/100km (o kWh/100km se elettrico)
  veicoliStandard: [
    { id: 'clio5_benzina', nome: 'Clio V', carburante: 'Benzina', categoria: 'City', consumo: 5.8, elettrico: false, tipoMezzo: 'auto' },
    { id: 'golf7_diesel', nome: 'Golf VII', carburante: 'Diesel', categoria: 'City', consumo: 4.9, elettrico: false, tipoMezzo: 'auto' },
    { id: 'sandero_diesel', nome: 'Dacia Sandero', carburante: 'Diesel', categoria: 'Suv', consumo: 4.5, elettrico: false, tipoMezzo: 'auto' },
    { id: 'panda_benzina', nome: 'Fiat Panda', carburante: 'Benzina', categoria: 'City', consumo: 5.5, elettrico: false, tipoMezzo: 'auto' },
    { id: 'tesla_m3', nome: 'Tesla Model 3', carburante: 'Elettrico', categoria: 'Berlina', consumo: 15.0, elettrico: true, tipoMezzo: 'auto' },
    { id: 'generico_benzina', nome: 'Auto generica', carburante: 'Benzina', categoria: 'Generica', consumo: 6.5, elettrico: false, tipoMezzo: 'auto' },
    { id: 'generico_diesel', nome: 'Auto generica', carburante: 'Diesel', categoria: 'Generica', consumo: 5.5, elettrico: false, tipoMezzo: 'auto' },
    { id: 'moto_media', nome: 'Moto', carburante: 'Benzina', categoria: 'Moto', consumo: 4.0, elettrico: false, tipoMezzo: 'moto' },
  ],
  preferenzeStrada: ['Nessun pedaggio', 'Evita autostrade', 'Percorso più veloce', 'Percorso più economico'],
  tipiAlimentazione: [
    { id: 'benzina', label: 'Benzina' },
    { id: 'diesel', label: 'Diesel' },
    { id: 'elettrico', label: 'Elettrico' },
  ],
  paesiViaggio: [
    { id: 'italia', label: 'Italia' },
    { id: 'francia', label: 'Francia' },
    { id: 'germania', label: 'Germania' },
    { id: 'spagna', label: 'Spagna' },
    { id: 'svizzera', label: 'Svizzera' },
    { id: 'austria', label: 'Austria' },
    { id: 'altro', label: 'Altro paese estero' },
  ],
  // ⚠️ Prezzi medi placeholder (€/litro benzina-diesel, €/kWh elettrico). In futuro da API prezzi carburante.
  prezziCarburante: {
    italia: { benzina: 1.85, diesel: 1.75, elettrico: 0.50 },
    francia: { benzina: 1.90, diesel: 1.85, elettrico: 0.55 },
    germania: { benzina: 1.80, diesel: 1.75, elettrico: 0.48 },
    spagna: { benzina: 1.65, diesel: 1.55, elettrico: 0.42 },
    svizzera: { benzina: 1.95, diesel: 2.05, elettrico: 0.35 },
    austria: { benzina: 1.60, diesel: 1.65, elettrico: 0.45 },
    altro: { benzina: 1.80, diesel: 1.75, elettrico: 0.50 },
  },
};

const SEGMENTI = [
  { id: 'trasporto', label: 'Trasporto', colore: CONFIG.colori.trasporto },
  { id: 'alloggio', label: 'Alloggio', colore: CONFIG.colori.alloggio },
  { id: 'attivita', label: 'Attività', colore: CONFIG.colori.attivita },
  { id: 'servizi', label: 'Servizi', colore: CONFIG.colori.servizi },
];

/* ============================================================
   UTILITY DATE / NOTTI
   ============================================================ */
const toDate = (s) => (s ? new Date(s + 'T00:00:00') : null);

// Restituisce l'elenco delle notti (data di pernottamento) tra due date.
// Una notte è identificata dalla data in cui ci si corica.
function nottiTra(da, a) {
  const d1 = toDate(da);
  const d2 = toDate(a);
  if (!d1 || !d2 || d2 <= d1) return [];
  const out = [];
  const cur = new Date(d1);
  while (cur < d2) {
    out.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

const fmtData = (s) => {
  const d = toDate(s);
  if (!d) return '—';
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
};

const euro = (n) => `€ ${Number(n || 0).toFixed(0)}`;

/* ============================================================
   STATO INIZIALE DELL'ESPERIENZA
   Rispecchia lo schema Supabase v2 (vedi TODO migration in PROGETTO.md)
   ============================================================ */
const STATO_INIZIALE = {
  anagrafica: {
    origine: '',
    destinazione: '',
    data_partenza: '',
    data_ritorno: '',
    num_adulti: 2,
    multi_tappa: false,
    num_bambini: 0,
    eta_bambini: '',
    con_animali: false,
    animali: '',
  },
  trasporto: [],   // array ordinato di tratte
  alloggio: [],    // array di strutture
  notti_amici: [], // array di date-notte marcate "da amici/familiari"
  attivita: [],
  servizi: [],
  fascia_prezzo: { min: '', max: '' },
  titolo: '',
};

const BREAKPOINT_MOBILE = 768;

function useIsMobile() {
  const mql = () => window.matchMedia(`(max-width: ${BREAKPOINT_MOBILE}px)`);
  const [isMobile, setIsMobile] = useState(() => mql().matches);
  useEffect(() => {
    const mq = mql();
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isMobile;
}

/* ============================================================
   COMPONENTE PRINCIPALE
   ============================================================ */
export default function CreaEsperienza() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [esp, setEsp] = useState(STATO_INIZIALE);
  const [vista, setVista] = useState('anagrafica'); // anagrafica | anteprima (panoramica dei 4 segmenti) | trasporto | alloggio | attivita | servizi | prezzo
  const [riepilogoAperto, setRiepilogoAperto] = useState(() => window.innerWidth > BREAKPOINT_MOBILE);
  const [checkAperto, setCheckAperto] = useState(false);
  const [aiuto, setAiuto] = useState(null);   // risposta del check "Non lo so, aiutami"
  const [aiutoCaricamento, setAiutoCaricamento] = useState(false);
  const [salvataggio, setSalvataggio] = useState({ stato: 'idle', messaggio: '' });
  const [indietroSegmento, setIndietroSegmento] = useState(null);
  // segmenti tolti dalla panoramica con la x (qui e non in AnteprimaFinale, così restano tolti entrando/uscendo dai segmenti)
  const [segmentiRimossi, setSegmentiRimossi] = useState([]);
  // elemento aperto con "modifica" dalla panoramica: { segmento, indice }. Si azzera tornando alla panoramica.
  const [modifica, setModifica] = useState(null);
  useEffect(() => { if (vista === 'anteprima') setModifica(null); }, [vista]);
  const indiceModifica = (seg) => (modifica?.segmento === seg ? modifica.indice : null);

  const segmentoAttivo = SEGMENTI.find((s) => s.id === vista);
  const coloreAttivo = segmentoAttivo ? segmentoAttivo.colore : CONFIG.colori.dark;

  const patch = (chiave, valore) => setEsp((e) => ({ ...e, [chiave]: valore }));

  /* -------- Derivati -------- */
  const nottiViaggio = useMemo(
    () => nottiTra(esp.anagrafica.data_partenza, esp.anagrafica.data_ritorno),
    [esp.anagrafica.data_partenza, esp.anagrafica.data_ritorno]
  );

  // Copertura notti: strutture + servizi con copre_alloggio + notti da amici
  const copertura = useMemo(() => {
    const coperte = new Set();
    esp.alloggio.forEach((a) => nottiTra(a.date_da, a.date_a).forEach((n) => coperte.add(n)));
    esp.servizi
      .filter((s) => s.copre_alloggio)
      .forEach((s) => nottiTra(s.data_inizio, s.data_fine).forEach((n) => coperte.add(n)));
    esp.notti_amici.forEach((n) => coperte.add(n));
    const scoperte = nottiViaggio.filter((n) => !coperte.has(n));
    return { coperte, scoperte };
  }, [esp.alloggio, esp.servizi, esp.notti_amici, nottiViaggio]);

  // Sovrapposizione strutture: max 1 struttura per notte
  const nottiSovrapposte = useMemo(() => {
    const visto = new Set();
    const dup = new Set();
    esp.alloggio.forEach((a) =>
      nottiTra(a.date_da, a.date_a).forEach((n) => {
        if (visto.has(n)) dup.add(n);
        visto.add(n);
      })
    );
    return [...dup];
  }, [esp.alloggio]);

  const costoTotale = useMemo(() => {
    const somma = (arr, campo) =>
      arr.reduce((t, x) => t + (parseFloat(x[campo]) || 0), 0);
    return (
      esp.trasporto.reduce((t, x) => t + (parseFloat(x.prezzo) || 0) + (parseFloat(x.costo_stimato) || 0), 0) +
      somma(esp.alloggio, 'prezzo') +
      somma(esp.attivita, 'prezzo') +
      somma(esp.servizi, 'prezzo')
    );
  }, [esp]);

  const segmentiCompilati = SEGMENTI.filter((s) => (esp[s.id] || []).length > 0);

  // Veicolo ereditabile dal trasporto (dipendenza trasporto → servizi)
  const veicoloDalTrasporto = useMemo(
    () => esp.trasporto.find((t) => CONFIG.mezziSuStrada.includes(t.mezzo) && t.veicolo),
    [esp.trasporto]
  );

  // Noleggio che copre l'intero viaggio (dipendenza inversa servizi → trasporto)
  const noleggioCoincidente = useMemo(
    () =>
      esp.servizi.find(
        (s) =>
          s.data_inizio &&
          s.data_fine &&
          s.data_inizio === esp.anagrafica.data_partenza &&
          s.data_fine === esp.anagrafica.data_ritorno
      ),
    [esp.servizi, esp.anagrafica]
  );

  /* -------- Validazione minimi obbligatori -------- */
  const errori = useMemo(() => {
    const e = [];
    if (!esp.anagrafica.destinazione.trim()) e.push('Manca la destinazione.');
    if (nottiViaggio.length === 0) e.push('Le date del viaggio non sono valide: il ritorno deve essere dopo la partenza.');
    if (segmentiCompilati.length === 0) e.push('Compila almeno un segmento.');
    if (esp.fascia_prezzo.min === '' || esp.fascia_prezzo.max === '')
      e.push('La fascia di prezzo è obbligatoria.');
    else if (Number(esp.fascia_prezzo.min) > Number(esp.fascia_prezzo.max))
      e.push('Nella fascia di prezzo il minimo supera il massimo.');
    // Vincolo trasporto: se presente, servono andata + ritorno
    if (esp.trasporto.length > 0) {
      const tipi = esp.trasporto.map((t) => t.tipo);
      if (!tipi.includes('andata') || !tipi.includes('ritorno'))
        e.push('Il trasporto ha bisogno di almeno una tratta di andata e una di ritorno.');
    }
    if (nottiSovrapposte.length > 0)
      e.push(`Due strutture si sovrappongono sulle stesse notti (${nottiSovrapposte.length}). Una notte può avere una sola struttura.`);
    return e;
  }, [esp, nottiViaggio, segmentiCompilati, nottiSovrapposte]);

  /* -------- Check "Non lo so, aiutami" --------
     Ragiona per COPERTURA NOTTI, non per presenza del segmento alloggio.
     Le notti coperte da camper/van o da amici non generano falsi allarmi. */
  async function analizzaEsperienza() {
    setAiutoCaricamento(true);
    try {
      const risposta = await claude.messages.create({
        model: 'claude-haiku-4-5-20251001',
        max_tokens: 1024,
        system: `Sei l'assistente di OCTA e aiuti un utente a capire se il racconto della sua esperienza di viaggio è completo.
Regole di dipendenza tra segmenti da rispettare (per non generare falsi allarmi):
- Un servizio di noleggio camper/van/barca copre l'alloggio per le notti del noleggio: non segnalare l'alloggio come mancante per quelle notti.
- Le notti "da amici o familiari" sono legittime, a costo zero, e non richiedono alcun dettaglio.
- Il canone di noleggio (auto/moto/scooter) va nei Servizi, non nel Trasporto: il Trasporto contiene solo carburante e pedaggi.
Ragiona per COPERTURA NOTTI (notti_totali_viaggio vs notti_scoperte), non per la semplice presenza del segmento alloggio.
Fai al massimo 3 domande mirate e concrete, in italiano, rivolte direttamente all'utente (es. "Le notti del 6-7 dove hai dormito?"). Se l'esperienza sembra completa, imposta completa a true e restituisci un array domande vuoto.`,
        messages: [
          {
            role: 'user',
            content: `Stato dell'esperienza in costruzione (JSON):\n${JSON.stringify(
              {
                anagrafica: esp.anagrafica,
                notti_totali_viaggio: nottiViaggio.length,
                notti_scoperte: copertura.scoperte,
                trasporto: esp.trasporto,
                alloggio: esp.alloggio,
                notti_amici: esp.notti_amici,
                attivita: esp.attivita,
                servizi: esp.servizi,
              },
              null,
              2
            )}`,
          },
        ],
        output_config: {
          format: {
            type: 'json_schema',
            schema: {
              type: 'object',
              properties: {
                completa: { type: 'boolean' },
                domande: { type: 'array', items: { type: 'string' } },
              },
              required: ['completa', 'domande'],
              additionalProperties: false,
            },
          },
        },
      });

      const testo = risposta.content.find((b) => b.type === 'text')?.text || '{}';
      const { completa, domande } = JSON.parse(testo);
      setAiuto(completa || domande.length === 0 ? ['L’esperienza sembra completa. Puoi salvarla.'] : domande);
    } catch (err) {
      setAiuto(['Non sono riuscito a contattare l’assistente. Riprova tra poco.']);
    } finally {
      setAiutoCaricamento(false);
    }
  }

  /* -------- Salvataggio -------- */
  async function salva() {
    if (errori.length > 0) {
      setCheckAperto(true);
      return;
    }
    setSalvataggio({ stato: 'invio', messaggio: '' });

    const { data: { user } } = await supabase.auth.getUser();

    const record = {
      user_id: user.id,
      titolo: esp.titolo || `${esp.anagrafica.destinazione} — ${nottiViaggio.length} notti`,
      origine: esp.anagrafica.origine,
      destinazione: esp.anagrafica.destinazione,
      data_partenza: esp.anagrafica.data_partenza,
      data_ritorno: esp.anagrafica.data_ritorno,
      num_adulti: esp.anagrafica.num_adulti,
      multi_tappa: esp.anagrafica.multi_tappa,
      num_bambini: esp.anagrafica.num_bambini,
      eta_bambini: esp.anagrafica.num_bambini > 0 ? esp.anagrafica.eta_bambini : '',
      con_animali: esp.anagrafica.con_animali,
      animali: esp.anagrafica.con_animali ? esp.anagrafica.animali : '',
      durata_notti: nottiViaggio.length,
      fascia_prezzo: { min: Number(esp.fascia_prezzo.min), max: Number(esp.fascia_prezzo.max) },
      trasporto: esp.trasporto,
      alloggio: esp.alloggio,
      notti_amici: esp.notti_amici.length,
      attivita: esp.attivita,
      servizi: esp.servizi,
      is_pubblica: true,
      modalita: 'guidata',
      punti_assegnati:
        CONFIG.punti.guidata + segmentiCompilati.length * CONFIG.punti.bonusSegmentoCompilato,
    };

    try {
      const { error } = await supabase.from('esperienze').insert(record);
      if (error) throw error;
      setSalvataggio({ stato: 'ok', messaggio: 'Esperienza salvata.' });
      navigate('/bacheca');
    } catch (err) {
      setSalvataggio({ stato: 'errore', messaggio: err.message || 'Salvataggio non riuscito.' });
    }
  }

  /* ============================================================
     RENDER
     ============================================================ */
  return (
    <div style={S.pagina}>
      <style>{`
        .octa-card-btn, .octa-piu, .octa-piu-ripristina { transition: background-color 0.15s, color 0.15s, border-color 0.15s; }
        .octa-piu:hover { background: #fff !important; color: var(--seg) !important; border-color: #fff !important; }
        .octa-piu-ripristina:hover { background: var(--seg) !important; color: #fff !important; }
        .octa-card-btn:hover { background: #fff !important; color: var(--seg) !important; border-color: #fff !important; }
        .octa-mezzo-icona .octa-mezzo-svg { transition: fill 0.15s; }
        .octa-mezzo-icona:hover .octa-mezzo-svg { fill: #EB1C24 !important; }
        .octa-mezzo-icona .octa-mezzo-emoji { transition: filter 0.15s; }
        .octa-mezzo-icona:hover .octa-mezzo-emoji { filter: ${FILTRO_ROSSO} !important; }
        .octa-input-citta::placeholder { font-size: clamp(17px, 1.05vw, 21px); }
        .octa-tipo-icona .octa-tipo-svg { transition: fill 0.15s; }
        .octa-tipo-icona:hover .octa-tipo-svg { fill: var(--hover) !important; }
        .octa-riepilogo-btn { transition: opacity 0.15s; }
        .octa-riepilogo-btn:hover { opacity: 0.7; }
      `}</style>
      <Header
        isMobile={isMobile}
        onIndietro={
          indietroSegmento
            ? indietroSegmento
            : ['trasporto', 'alloggio', 'attivita', 'servizi', 'prezzo'].includes(vista)
            ? () => setVista('anteprima')
            : vista === 'anteprima'
            ? () => setVista('anagrafica')
            : undefined
        }
      />

      <div style={{ ...S.corpo, ...(isMobile ? { flexDirection: 'column' } : {}) }}>
        <Riepilogo
          esp={esp}
          aperto={riepilogoAperto}
          setAperto={setRiepilogoAperto}
          colore={coloreAttivo}
          costoTotale={costoTotale}
          segmentiCompilati={segmentiCompilati}
          vaiA={setVista}
          isMobile={isMobile}
        />

        <main style={S.centro}>
          {vista !== 'anagrafica' && (
            <BoxIlTuoViaggio
              anagrafica={esp.anagrafica}
              tratte={esp.trasporto}
              notti={nottiViaggio.length}
              isMobile={isMobile}
            />
          )}

          {vista === 'anagrafica' && (
            <Anagrafica
              valore={esp.anagrafica}
              onChange={(a) => patch('anagrafica', a)}
              onAvanti={() => setVista('trasporto')}
              nottiCalcolate={nottiViaggio.length}
              riepilogoAperto={riepilogoAperto}
              isMobile={isMobile}
            />
          )}

          {vista === 'trasporto' && (
            <Trasporto
              tratte={esp.trasporto}
              onChange={(v) => patch('trasporto', v)}
              anagrafica={esp.anagrafica}
              noleggioCoincidente={noleggioCoincidente}
              isMobile={isMobile}
              indiceModifica={indiceModifica('trasporto')}
              vaiA={setVista}
              registraIndietro={(fn) => setIndietroSegmento(() => fn)}
            />
          )}

          {vista === 'alloggio' && (
            <Alloggio
              strutture={esp.alloggio}
              onChange={(v) => patch('alloggio', v)}
              nottiViaggio={nottiViaggio}
              nottiAmici={esp.notti_amici}
              onNottiAmici={(v) => patch('notti_amici', v)}
              copertura={copertura}
              anagrafica={esp.anagrafica}
              isMobile={isMobile}
              indiceModifica={indiceModifica('alloggio')}
              vaiA={setVista}
              registraIndietro={(fn) => setIndietroSegmento(() => fn)}
            />
          )}

          {vista === 'attivita' && (
            <Attivita
              vaiA={setVista}
              isMobile={isMobile}
              registraIndietro={(fn) => setIndietroSegmento(() => fn)}
              lista={esp.attivita}
              indiceModifica={indiceModifica('attivita')}
              onChange={(v) => patch('attivita', v)}
              anagrafica={esp.anagrafica}
            />
          )}

          {vista === 'servizi' && (
            <Servizi
              vaiA={setVista}
              isMobile={isMobile}
              registraIndietro={(fn) => setIndietroSegmento(() => fn)}
              lista={esp.servizi}
              indiceModifica={indiceModifica('servizi')}
              onChange={(v) => patch('servizi', v)}
              anagrafica={esp.anagrafica}
              veicoloDalTrasporto={veicoloDalTrasporto}
            />
          )}

          {vista === 'prezzo' && (
            <FasciaPrezzo
              valore={esp.fascia_prezzo}
              onChange={(v) => patch('fascia_prezzo', v)}
              titolo={esp.titolo}
              onTitolo={(v) => patch('titolo', v)}
              costoTotale={costoTotale}
            />
          )}

          {vista === 'anteprima' && (
            <AnteprimaFinale esp={esp} vaiA={setVista} costoTotale={costoTotale} onChange={patch} isMobile={isMobile}
              nottiViaggio={nottiViaggio} copertura={copertura}
              segmentiRimossi={segmentiRimossi} setSegmentiRimossi={setSegmentiRimossi}
              onModifica={(segmento, indice) => { setModifica({ segmento, indice }); setVista(segmento); }}
              onCheck={() => { setAiuto(null); setCheckAperto(true); }} />
          )}
        </main>
      </div>

      {checkAperto && (
        <CheckCompletamento
          errori={errori}
          aiuto={aiuto}
          aiutoCaricamento={aiutoCaricamento}
          onAiuto={analizzaEsperienza}
          onContinua={() => { setCheckAperto(false); setVista('anteprima'); }}
          onFinito={() => {
            if (errori.some((e) => e.includes('fascia di prezzo'))) { setCheckAperto(false); setVista('prezzo'); return; }
            salva();
          }}
          salvataggio={salvataggio}
          onChiudi={() => setCheckAperto(false)}
        />
      )}

      <Footer />
    </div>
  );
}

/* ============================================================
   LAYOUT COMUNE
   ============================================================ */
function Header({ isMobile, onIndietro }) {
  const [menuAperto, setMenuAperto] = useState(false);
  return (
    <header style={S.header}>
      <button style={S.linkIndietro} onClick={onIndietro || (() => window.history.back())} aria-label="Indietro" title="Indietro">
        ←
      </button>
      <a href="/"><img src="/images/logo_bianco.png" alt="OCTA" style={S.logoHeaderCentrato} /></a>
      <nav style={{ ...S.navHeader, alignItems: 'center' }}>
        {!isMobile && (
          <>
            <a href="/profilo" title="Il mio profilo" style={{ display: 'flex', alignItems: 'center' }}>
              <img src="/images/profilo.svg" alt="Profilo" style={{ height: 20, filter: 'brightness(0) invert(1)' }} />
            </a>
            <a href="/impostazioni" title="Impostazioni" style={{ display: 'flex', alignItems: 'center' }}>
              <img src="/images/impostazioni.svg" alt="Impostazioni" style={{ height: 20, filter: 'brightness(0) invert(1)' }} />
            </a>
          </>
        )}
        <div style={{ position: 'relative' }}>
          <button style={S.linkHeader} onClick={() => setMenuAperto(v => !v)}>
            Menù <span style={{ fontSize: 9, opacity: 0.7 }}>{menuAperto ? '▲' : '▼'}</span>
          </button>
          {menuAperto && (
            <>
              <div onClick={() => setMenuAperto(false)} style={{ position: 'fixed', inset: 0, zIndex: 199 }} />
              <div style={{ position: 'absolute', top: 'calc(100% + 10px)', right: 0, backgroundColor: '#0a1628', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12, padding: 6, minWidth: 190, zIndex: 200, boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
                <a href="/profilo" onClick={() => setMenuAperto(false)} style={{ display: 'block', padding: '9px 14px', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>Il mio profilo</a>
                <a href="/impostazioni" onClick={() => setMenuAperto(false)} style={{ display: 'block', padding: '9px 14px', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>Impostazioni</a>
                <div style={{ height: 1, backgroundColor: 'rgba(255,255,255,0.12)', margin: '6px 4px' }} />
                <a href="/bacheca" onClick={() => setMenuAperto(false)} style={{ display: 'block', padding: '9px 14px', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>Vetrina</a>
                <a href="/come-funziona" onClick={() => setMenuAperto(false)} style={{ display: 'block', padding: '9px 14px', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>Come funziona</a>
                <div style={{ height: 1, backgroundColor: 'rgba(255,255,255,0.12)', margin: '6px 4px' }} />
                {[{ label: 'Trasporto', color: '#EB1C24', href: '/trasporto' }, { label: 'Alloggio', color: '#0071BA', href: '/alloggio' }, { label: 'Attività', color: '#FF5B00', href: '/attivita' }, { label: 'Servizi', color: '#009045', href: '/servizi' }].map(seg => (
                  <a key={seg.href} href={seg.href} onClick={() => setMenuAperto(false)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: seg.color, flexShrink: 0 }} />{seg.label}
                  </a>
                ))}
              </div>
            </>
          )}
        </div>
      </nav>
    </header>
  );
}

function Footer() {
  return (
    <footer style={S.footer}>
      {['COME FUNZIONA?', 'CONTATTI', 'PRIVACY', 'INFORMAZIONI LEGALI', 'CONDIZIONI'].map((v) => (
        <span key={v} style={S.voceFooter}>{v}</span>
      ))}
      <span style={{ ...S.logo, fontSize: 16, marginLeft: 'auto' }}>OCTA</span>
    </footer>
  );
}

function BoxIlTuoViaggio({ anagrafica, tratte, notti, isMobile }) {
  const [aperto, setAperto] = useState(false);

  const tappeIntermedie = useMemo(() => {
    const arrivi = (tratte || []).map((t) => t.a).filter(Boolean);
    const uniche = [];
    arrivi.forEach((a, i) => {
      if (i === arrivi.length - 1) return; // l'ultima tratta porta alla destinazione finale
      if (a === anagrafica.origine || a === anagrafica.destinazione) return;
      if (!uniche.includes(a)) uniche.push(a);
    });
    return uniche;
  }, [tratte, anagrafica.origine, anagrafica.destinazione]);

  const persone = (Number(anagrafica.num_adulti) || 0) + (Number(anagrafica.num_bambini) || 0) + (anagrafica.con_animali ? 1 : 0);
  const tappe = [anagrafica.origine || '—', ...tappeIntermedie, anagrafica.destinazione || '—'];

  const contenuto = (
    <div style={S.boxViaggioRigaOrizzontale}>
      <strong>{tappe.join(' → ')}</strong>
      <span style={S.boxViaggioSep}>·</span>
      <span>{notti} notti</span>
      <span style={S.boxViaggioSep}>·</span>
      <span>{persone} persone</span>
    </div>
  );

  if (isMobile) {
    return (
      <div style={S.boxViaggioMobile}>
        <button style={S.boxViaggioToggle} onClick={() => setAperto((v) => !v)}>
          Info base <span>{aperto ? '▲' : '▼'}</span>
        </button>
        {aperto && <div style={S.boxViaggioMobileContenuto}>{contenuto}</div>}
      </div>
    );
  }

  return <div style={S.boxViaggio}>{contenuto}</div>;
}

/* ============================================================
   PANNELLO RIEPILOGO
   Colore = segmento attivo. Collassato = barra verticale.
   ============================================================ */
function Riepilogo({ esp, aperto, setAperto, colore, costoTotale, segmentiCompilati, vaiA, isMobile }) {
  const card = (chiave, icona, titolo, sottotitolo, costo) => (
    <div key={chiave} style={S.cardRiepilogo}>
      <div style={S.cardTitolo}>
        {icona}
        <span>{titolo}</span>
      </div>
      <div style={S.cardSottotitolo}>{sottotitolo}</div>
      {costo ? <div style={S.cardCosto}>{euro(costo)}</div> : null}
    </div>
  );

  const stileBox = isMobile && aperto
    ? { ...S.riepilogoBox, position: 'fixed', inset: 0, width: '100%', minHeight: '100vh', overflowY: 'auto', zIndex: 250, borderColor: colore }
    : isMobile
    ? { ...S.riepilogoBox, width: '100%', minHeight: 'auto', borderTop: 'none', borderColor: colore }
    : { ...S.riepilogoBox, width: aperto ? 290 : 46, borderColor: colore };

  return (
    <aside style={stileBox}>
      {!aperto && isMobile ? (
        <div className="octa-riepilogo-btn" style={{ ...S.riepilogoChiusoMobile, background: colore }} onClick={() => setAperto(true)}>
          <span style={S.riepilogoOrizzontale}>RIEPILOGO</span>
          <div style={S.iconeSegmentiMobile}>
            {segmentiCompilati.map((s) => (
              <span key={s.id} style={S.pallinoSegmento} title={s.label}>&#9679;</span>
            ))}
          </div>
          <span style={S.frecciaRiepilogo}>&#9662;</span>
        </div>
      ) : !aperto ? (
        <div className="octa-riepilogo-btn" style={{ ...S.riepilogoChiuso, background: colore }} onClick={() => setAperto(true)}>
          <span style={S.frecciaRiepilogo}>»</span>
          <span style={S.riepilogoVerticale}>{'RIEPILOGO'.split('').join(' ')}</span>
          <div style={S.iconeSegmenti}>
            {segmentiCompilati.map((s) => (
              <span key={s.id} style={S.pallinoSegmento} title={s.label}>●</span>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div style={{ ...S.riepilogoHead, background: colore }}>
            <span>RIEPILOGO</span>
            <button className="octa-riepilogo-btn" style={S.chiudiRiepilogo} onClick={() => setAperto(false)}>«</button>
          </div>

          <div style={S.riepilogoCorpo}>
            {esp.trasporto.map((t, i) => {
              const compagniaLabel = CONFIG.compagnieTreno.find((cm) => cm.id === t.compagnia_treno)?.label;
              const dettagli = [
                TIPO_TRATTA_LABEL[t.tipo],
                fmtData(t.data),
                t.codice_volo,
                t.km ? `${t.km} km` : null,
                t.fascia_tariffa,
                t.classe_volo,
                compagniaLabel,
                t.classe_treno,
              ].filter(Boolean).join(' · ');
              return card(`t${i}`, <IconaMezzo mezzo={t.mezzo} colore={CONFIG.colori.trasporto} size={18} />, `${t.da || '—'} → ${t.a || '—'}`,
                dettagli,
                (parseFloat(t.prezzo) || 0) + (parseFloat(t.costo_stimato) || 0));
            })}
            {esp.alloggio.map((a, i) =>
              card(`a${i}`, <IconaAlloggio tipo={a.tipo} colore={CONFIG.colori.alloggio} size={18} />, a.nome || 'Struttura',
                [`${fmtData(a.date_da)} – ${fmtData(a.date_a)}`, `${nottiTra(a.date_da, a.date_a).length} notti`, a.fascia_prezzo]
                  .filter(Boolean).join(' · '))
            )}
            {esp.notti_amici.length > 0 &&
              card('amici', '🏡', 'Da amici o familiari', `${esp.notti_amici.length} notti · nessun costo`, 0)}
            {esp.attivita.map((a, i) => card(`at${i}`, '🎟️', a.nome || 'Attività', a.luogo || '', a.prezzo))}
            {esp.servizi.map((s, i) =>
              card(`s${i}`, '🧰', CONFIG.tipiServizio.find((t) => t.id === s.tipo)?.label || 'Servizio',
                s.data_inizio ? `${fmtData(s.data_inizio)} – ${fmtData(s.data_fine)}` : '', s.prezzo)
            )}

            {esp.trasporto.length + esp.alloggio.length + esp.attivita.length + esp.servizi.length === 0 && (
              <p style={S.vuoto}>Nessun elemento. Comincia da un segmento qui sotto.</p>
            )}

            <div style={S.totale}>
              <span>COSTO INDICATIVO TOTALE</span>
              <strong style={{ color: colore }}>{euro(costoTotale)}</strong>
            </div>

            {SEGMENTI.map((s) => (
              <button
                key={s.id}
                className="octa-riepilogo-btn"
                style={{ ...S.bottoneAggiungi, background: s.colore }}
                onClick={() => { setAperto(false); vaiA(s.id); }}
              >
                Aggiungi {s.label}
              </button>
            ))}

            <button className="octa-riepilogo-btn" style={S.bottoneInfBase} onClick={() => { setAperto(false); vaiA('anagrafica'); }}>
              Inf. base
            </button>
            <button
              className="octa-riepilogo-btn"
              style={{ ...S.bottoneInfBase, marginTop: 8, background: CONFIG.colori.dark, color: '#fff', borderColor: CONFIG.colori.dark }}
              onClick={() => { setAperto(false); vaiA('anteprima'); }}
            >
              Anteprima pacchetto
            </button>
          </div>
        </>
      )}
    </aside>
  );
}

/* ============================================================
   ANAGRAFICA VIAGGIO
   ============================================================ */
function Anagrafica({ valore, onChange, onAvanti, nottiCalcolate, riepilogoAperto, isMobile }) {
  const set = (k, v) => onChange({ ...valore, [k]: v });
  const pronta = valore.destinazione.trim() && nottiCalcolate > 0;
  const sidebarWidth = riepilogoAperto ? 290 : 46;

  const stileSezione = isMobile
    ? S.sezione
    : {
        ...S.sezione,
        margin: 0,
        position: 'absolute',
        top: 40,
        left: `calc(50vw - ${sidebarWidth}px)`,
        transform: 'translateX(-50%)',
        width: 'min(720px, 92vw)',
      };

  return (
    <section style={stileSezione}>
      <h1 style={{ ...S.h1, textAlign: 'center', textTransform: 'uppercase' }}>Raccontaci il tuo viaggio</h1>
      <p style={{ ...S.sottotitolo, textAlign: 'center' }}>
        Partiamo dalle basi. Queste informazioni vincolano le date di tratte, alloggi e servizi.
      </p>

      <div style={{ ...S.griglia2, marginTop: 36 }}>
        <Campo center label="Da dove sei partito">
          <input style={{ ...S.input, textAlign: 'center' }} value={valore.origine} onChange={(e) => set('origine', e.target.value)} placeholder="Milano" />
        </Campo>
        <Campo center label="Dove sei andato">
          <input style={{ ...S.input, textAlign: 'center' }} value={valore.destinazione} onChange={(e) => set('destinazione', e.target.value)} placeholder="Napoli" />
        </Campo>
        <Campo center label="Partenza">
          <input type="date" style={{ ...S.input, textAlign: 'center' }} value={valore.data_partenza} onChange={(e) => set('data_partenza', e.target.value)} />
        </Campo>
        <Campo center label="Ritorno">
          <input type="date" style={{ ...S.input, textAlign: 'center' }} value={valore.data_ritorno} onChange={(e) => set('data_ritorno', e.target.value)} />
        </Campo>
        <Campo center label="Adulti">
          <input type="number" min="1" style={{ ...S.input, textAlign: 'center' }} value={valore.num_adulti} onChange={(e) => set('num_adulti', Number(e.target.value))} />
        </Campo>
        <Campo center label="Bambini">
          <input type="number" min="0" style={{ ...S.input, textAlign: 'center' }} value={valore.num_bambini} onChange={(e) => set('num_bambini', Number(e.target.value))} />
        </Campo>
        {valore.num_bambini > 0 && (
          <Campo center label="Età dei bambini">
            <input style={{ ...S.input, textAlign: 'center' }} value={valore.eta_bambini} onChange={(e) => set('eta_bambini', e.target.value)} placeholder="3, 7" />
          </Campo>
        )}
        <Campo center label="Struttura del viaggio">
          <label style={S.checkRiga}>
            <input type="checkbox" checked={valore.multi_tappa} onChange={(e) => set('multi_tappa', e.target.checked)} />
            Il viaggio aveva più tappe
          </label>
        </Campo>
        <Campo center label="Animali">
          <label style={S.checkRiga}>
            <input type="checkbox" checked={valore.con_animali} onChange={(e) => set('con_animali', e.target.checked)} />
            Ho viaggiato con un animale
          </label>
        </Campo>
        {valore.con_animali && (
          <Campo center label="Con quale animale">
            <input style={{ ...S.input, textAlign: 'center' }} value={valore.animali} onChange={(e) => set('animali', e.target.value)} placeholder="Cane" />
          </Campo>
        )}
      </div>

      {nottiCalcolate > 0 && <p style={{ ...S.nota, textAlign: 'center' }}>Sono {nottiCalcolate} notti.</p>}

      <div style={{ textAlign: 'center', marginTop: 28 }}>
        <button style={{ ...S.bottonePrimario, opacity: pronta ? 1 : 0.4 }} disabled={!pronta} onClick={onAvanti}>
          Costruisci
        </button>
      </div>
    </section>
  );
}

/* ============================================================
   HUB — schermata centrale
   ============================================================ */
/* ============================================================
   ANTEPRIMA FINALE — tutti e 4 i segmenti, anche se non compilati
   ============================================================ */
const ANTEPRIMA_SEGMENTI = [
  { id: 'trasporto', label: 'Trasporto', colore: CONFIG.colori.trasporto, icona: '/images/trasporto_icona.png' },
  { id: 'alloggio', label: 'Alloggio', colore: CONFIG.colori.alloggio, icona: '/images/alloggio_icona.png' },
  { id: 'attivita', label: 'Attività', colore: CONFIG.colori.attivita, icona: '/images/attivita_icona.png' },
  { id: 'servizi', label: 'Servizi', colore: CONFIG.colori.servizi, icona: '/images/servizi_icona.png' },
];

/* Card dei risultati nella panoramica dei 4 segmenti.
   Riferimento: design/Raggruppa 1708.svg/.png (tavola Adobe XD "anteprima finale"). */
const ANTEPRIMA_TINTA = {
  [CONFIG.colori.trasporto]: 'rgba(235,28,36,0.45)',
  [CONFIG.colori.alloggio]: 'rgba(0,113,186,0.45)',
  [CONFIG.colori.attivita]: 'rgba(255,91,0,0.45)',
  [CONFIG.colori.servizi]: 'rgba(0,144,69,0.45)',
};
// Altezza minima del contenuto di ogni segmento nella panoramica: fissa la riga comune dei +
// (≈ metà del corpo di un segmento vuoto, che è alto 580px meno l'intestazione).
const RIGA_PIU = 200;
const ETICHETTA_TRATTA = { andata: 'Viaggio di andata', ritorno: 'Viaggio di ritorno', spostamento: 'Spostamento interno' };

const euroCent = (n) => Number(n).toFixed(2).replace('.', ',');
const fmtDataNum = (s) => {
  if (!s) return '';
  const [a, m, g] = s.split('-');
  return `${g}/${m}/${a}`;
};

// Al passaggio del mouse i colori si invertono: sfondo bianco, testo nel colore del segmento (--seg, vedi .octa-card-btn).
function BottoniCard({ onElimina, onModifica, dettagli, aperto, onDettagli, mini, colore }) {
  const st = { ...(mini ? S.cardBottoneMini : S.cardBottone), '--seg': colore };
  return (
    <span style={mini ? S.cardBottoniMini : S.cardBottoni}>
      <button className="octa-card-btn" style={st} onClick={onModifica}>{mini ? 'Modifica' : 'modifica'}</button>
      <button className="octa-card-btn" style={st} onClick={onElimina}>{mini ? 'Elimina' : 'elimina'}</button>
      {dettagli && (
        <button className="octa-card-btn" style={{ ...st, ...(aperto ? { background: 'rgba(255,255,255,0.25)' } : {}) }} onClick={onDettagli}>
          {mini ? 'Dettagli' : 'dettagli'}
        </button>
      )}
    </span>
  );
}

function CardTratta({ item, onElimina, onModifica }) {
  const [aperto, setAperto] = useState(false);
  const [partenza, arrivo] = (item.orario || '').split(/\s*[–-]\s*/);
  const mezzoLabel = CONFIG.mezzi.find((m) => m.id === item.mezzo)?.label;
  const compagniaLabel = CONFIG.compagnieTreno.find((cm) => cm.id === item.compagnia_treno)?.label;
  const importo = (parseFloat(item.prezzo) || 0) + (parseFloat(item.costo_stimato) || 0);
  const vettore = item.codice_volo || [compagniaLabel, item.numero_treno].filter(Boolean).join(' ');
  const extra = [item.classe_volo || item.classe_treno, item.fascia_tariffa && `Tariffa ${item.fascia_tariffa}`,
    (item.tappe || []).length > 0 && `Tappe: ${item.tappe.join(', ')}`, item.km && `${item.km} km`,
    item.pedaggi && `Pedaggi € ${euroCent(item.pedaggi)}`].filter(Boolean);

  return (
    <div style={S.cardTratta}>
      <div style={S.cardTrattaRiga1}>
        <span style={S.cardEtichetta}>{ETICHETTA_TRATTA[item.tipo] || 'Tratta'}</span>
        <IconaMezzo mezzo={item.mezzo} colore="#fff" size={22} />
        <BottoniCard colore={CONFIG.colori.trasporto} onElimina={onElimina} onModifica={onModifica} dettagli aperto={aperto} onDettagli={() => setAperto((v) => !v)} />
      </div>
      <div style={S.cardTrattaRiga2}>
        <div style={S.cardTrattaPunto}>
          <span style={S.cardTrattaOra}>{partenza || fmtData(item.data) || '—'}</span>
          <span style={S.cardTrattaCitta}>{item.da}</span>
        </div>
        <div style={S.cardTrattaCentro}>
          <span style={S.cardTrattaMedio}>{item.durata || ''}</span>
          <span style={S.cardTrattaLinea} />
          <span style={S.cardTrattaMedio}>{mezzoLabel}</span>
        </div>
        <div style={S.cardTrattaPunto}>
          <span style={S.cardTrattaOra}>{arrivo || ''}</span>
          <span style={S.cardTrattaCitta}>{item.a}</span>
        </div>
        <span style={S.cardTrattaBarra} />
        <span style={S.cardTrattaPrezzo}>{importo > 0 ? `€ ${euroCent(importo)}` : (item.fascia_tariffa || '')}</span>
      </div>
      {aperto && (
        <div style={S.cardTrattaDettagli}>
          {vettore && <div style={S.cardTrattaVettore}>{vettore}</div>}
          <div style={S.cardTrattaPercorso}>
            <span style={S.cardTrattaDurataLato}>{item.durata}</span>
            <div style={S.cardTrattaTimeline}>
              <div style={S.cardTrattaTappa}><span style={S.cardTrattaPallino} /><strong>{partenza}</strong>&nbsp;&nbsp;<em>{item.da}</em></div>
              <div style={S.cardTrattaTappa}><span style={S.cardTrattaPallino} /><strong>{arrivo}</strong>&nbsp;&nbsp;<em>{item.a}</em></div>
            </div>
          </div>
          {extra.map((e) => <div key={e} style={S.cardTrattaExtra}>{e}</div>)}
          {item.data && (
            <div style={{ ...S.cardTrattaExtra, fontSize: 12, marginTop: 6 }}>
              Partenza: {fmtData(item.data)}{item.durata ? ` - Durata del viaggio: ${item.durata}` : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* Con un solo alloggio la foto si vede per intero; con più alloggi resta solo il link "Vedi foto".
   ⚠️ INNESTO 7 — item.foto arriverà da Google Places. */
function CardAlloggio({ item, onElimina, onModifica, ospiti, unico }) {
  const [aperto, setAperto] = useState(false);
  const notti = nottiTra(item.date_da, item.date_a).length;
  const servizi = item.servizi_usati || [];
  return (
    <div style={S.cardAlloggio}>
      <div style={S.cardAlloggioRiga1}>
        <span style={S.cardAlloggioNome}>{item.nome || CONFIG.tipiAlloggio.find((t) => t.id === item.tipo)?.label || 'Alloggio'}</span>
        <BottoniCard colore={CONFIG.colori.alloggio} onElimina={onElimina} onModifica={onModifica} dettagli={servizi.length > 0} aperto={aperto} onDettagli={() => setAperto((v) => !v)} />
      </div>
      {item.fascia_prezzo && (
        <div style={S.cardAlloggioPrezzo}>{item.fascia_prezzo} per notte - {ospiti} {ospiti === 1 ? 'ospite' : 'ospiti'}</div>
      )}
      <div style={S.cardAlloggioDate}>
        <span>{fmtDataNum(item.date_da)}</span>
        <span style={S.cardAlloggioSlash} />
        <span>{fmtDataNum(item.date_a)}</span>
      </div>
      <div style={S.cardAlloggioNotti}>{notti} {notti === 1 ? 'notte' : 'notti'}</div>
      {item.foto && (unico ? (
        <img src={item.foto} alt={item.nome} style={S.cardAlloggioFoto} />
      ) : (
        <a href={item.foto} target="_blank" rel="noreferrer" style={S.cardLink}>Vedi foto</a>
      ))}
      {aperto && (
        <ul style={S.cardAlloggioServizi}>
          {servizi.map((sv) => <li key={sv}>{sv}</li>)}
        </ul>
      )}
    </div>
  );
}

// Attività e Servizi: card piccole su 2 colonne.
function CardMini({ segmento, item, onElimina, onModifica }) {
  const [aperto, setAperto] = useState(false);
  const tipi = segmento === 'attivita' ? CONFIG.tipiAttivita : CONFIG.tipiServizio;
  const tipoLabel = tipi.find((t) => t.id === item.tipo)?.label || (segmento === 'attivita' ? 'Attività' : 'Servizio');
  const titolo = segmento === 'attivita'
    ? (item.nome || tipoLabel)
    : `${tipoLabel}${item.descrizione ? ` "${item.descrizione}"` : ''}`;
  const quando = segmento === 'attivita'
    ? fmtDataNum(item.data)
    : [fmtDataNum(item.data_inizio), fmtDataNum(item.data_fine)].filter(Boolean).join('  -  ');
  const dettagli = [segmento === 'attivita' && item.luogo, item.note].filter(Boolean);
  const importo = parseFloat(item.prezzo);

  return (
    <div style={S.cardMini}>
      <div style={S.cardMiniTesta}>
        <span style={S.cardMiniTitolo}>{titolo}</span>
        <BottoniCard mini colore={CONFIG.colori[segmento]} onElimina={onElimina} onModifica={onModifica} dettagli={dettagli.length > 0} aperto={aperto} onDettagli={() => setAperto((v) => !v)} />
      </div>
      <div style={S.cardMiniRiga}>{importo > 0 ? `${euroCent(importo)} €` : ''}</div>
      <div style={S.cardMiniRiga}>{quando}</div>
      {aperto && (
        <div style={S.cardMiniDettagli}>
          {dettagli.map((d) => <div key={d}>- {d}</div>)}
        </div>
      )}
    </div>
  );
}

function AnteprimaCard({ segmento, item, onElimina, onModifica, ospiti, unico }) {
  if (segmento === 'trasporto') return <CardTratta item={item} onElimina={onElimina} onModifica={onModifica} />;
  if (segmento === 'alloggio') return <CardAlloggio item={item} onElimina={onElimina} onModifica={onModifica} ospiti={ospiti} unico={unico} />;
  return <CardMini segmento={segmento} item={item} onElimina={onElimina} onModifica={onModifica} />;
}

function AnteprimaFinale({ esp, vaiA, costoTotale, onChange, isMobile, onCheck, nottiViaggio, copertura, segmentiRimossi, setSegmentiRimossi, onModifica }) {
  const rimuovi = (segId, i) => onChange(segId, esp[segId].filter((_, j) => j !== i));
  const visibili = ANTEPRIMA_SEGMENTI.filter((s) => !segmentiRimossi.includes(s.id));
  const rimossi = ANTEPRIMA_SEGMENTI.filter((s) => segmentiRimossi.includes(s.id));

  // la x toglie l'intero riquadro (e il suo contenuto, così non pesa sul totale); ne resta sempre almeno uno
  const eliminaSegmento = (segId) => {
    onChange(segId, []);
    setSegmentiRimossi([...segmentiRimossi, segId]);
  };
  const ripristina = (segId) => setSegmentiRimossi(segmentiRimossi.filter((id) => id !== segId));

  return (
    <div style={S.anteprimaPagina}>
      <div style={{ ...S.anteprimaColonne, flexDirection: isMobile ? 'column' : 'row' }}>
        {visibili.map((s) => {
          const lista = esp[s.id] || [];
          return (
            // su desktop ogni colonna resta larga 1/4 (meno i 3 gap da 3px) anche se altri segmenti vengono eliminati
            <div key={s.id} style={{ ...S.anteprimaColonna, ...(isMobile ? {} : { flex: '0 0 calc((100% - 9px) / 4)' }) }}>
              <div style={{ ...S.anteprimaHeader, background: s.colore }}>
                <img src={s.icona} alt="" style={S.anteprimaHeaderIcona} />
                <span style={S.anteprimaHeaderTitolo}>{s.label}</span>
                {visibili.length > 1 && (
                  <button style={S.anteprimaChiudi} onClick={() => eliminaSegmento(s.id)}
                    title={`Elimina ${s.label}`} aria-label={`Elimina ${s.label}`}>×</button>
                )}
              </div>
              <div
                style={{
                  ...S.anteprimaCorpo, background: ANTEPRIMA_TINTA[s.colore], padding: 8,
                  justifyContent: isMobile && lista.length === 0 ? 'center' : 'flex-start',
                }}
              >
                {/* su desktop il contenuto occupa almeno RIGA_PIU: così tutti i + stanno sulla stessa riga,
                    e scendono più in basso solo se i risultati di quel segmento sono più lunghi */}
                <div style={{ ...S.anteprimaContenuto, ...(isMobile ? {} : { minHeight: RIGA_PIU }) }}>
                {s.id === 'attivita' || s.id === 'servizi' ? (
                  lista.length > 0 && (
                    <div style={S.griglieMini}>
                      {lista.map((item, i) => (
                        <AnteprimaCard key={i} segmento={s.id} item={item} onElimina={() => rimuovi(s.id, i)}
                          onModifica={() => onModifica(s.id, i)} />
                      ))}
                    </div>
                  )
                ) : lista.map((item, i) => (
                  <AnteprimaCard key={i} segmento={s.id} item={item} onElimina={() => rimuovi(s.id, i)}
                    onModifica={() => onModifica(s.id, i)}
                    ospiti={(esp.anagrafica?.num_adulti || 0) + (esp.anagrafica?.num_bambini || 0) || 1} unico={lista.length === 1} />
                ))}
                </div>
                <button className="octa-piu" style={{ ...S.anteprimaPiu, '--seg': s.colore }}
                  onClick={() => vaiA(s.id)} aria-label={`Aggiungi ${s.label}`}>+</button>
              </div>
            </div>
          );
        })}
        {/* un + per ogni segmento eliminato, nel colore del segmento, al centro dello spazio che occupava:
            si sceglie quale rimettere, e torna nella sua posizione originale */}
        {rimossi.map((s) => (
          <div key={s.id} style={{ ...S.anteprimaColonna, ...(isMobile ? {} : { flex: '0 0 calc((100% - 9px) / 4)' }) }}>
            {/* intestazione invisibile della stessa altezza: così questo + sta alla stessa altezza di quelli dei segmenti vuoti */}
            {!isMobile && (
              <div style={{ ...S.anteprimaHeader, visibility: 'hidden' }} aria-hidden="true">
                <img src={s.icona} alt="" style={S.anteprimaHeaderIcona} />
                <span style={S.anteprimaHeaderTitolo}>{s.label}</span>
              </div>
            )}
            <div style={{ ...S.anteprimaRipristino, ...(isMobile ? { padding: '16px 0' } : {}) }}>
              {!isMobile && <div style={{ minHeight: RIGA_PIU }} />}
              <button className="octa-piu-ripristina" style={{ ...S.anteprimaRipristinoPiu, border: `2px solid ${s.colore}`, color: s.colore, '--seg': s.colore }}
                onClick={() => ripristina(s.id)} aria-label={`Aggiungi ${s.label}`} title={`Aggiungi ${s.label}`}>+</button>
            </div>
          </div>
        ))}
      </div>

      <div style={S.anteprimaFooter}>
        <span style={S.anteprimaFooterLabel}>TOTALE SPESA INDICATIVO</span>
        <strong style={S.anteprimaFooterTotale}>{euro(costoTotale)}</strong>
      </div>

      {nottiViaggio.length > 0 && (
        <div style={{ ...S.barraNotti, padding: '0 16px' }}>
          <span style={S.barraNottiLabel}>Notti coperte</span>
          <div style={S.barraNottiTrack}>
            {nottiViaggio.map((n) => (
              <span
                key={n}
                title={fmtData(n)}
                style={{
                  ...S.nottePallino,
                  background: copertura.coperte.has(n) ? CONFIG.colori.alloggio : CONFIG.colori.bordo,
                }}
              />
            ))}
          </div>
          <span style={S.barraNottiConteggio}>
            {nottiViaggio.length - copertura.scoperte.length}/{nottiViaggio.length}
          </span>
        </div>
      )}

      <div style={{ ...S.rigaBottoni, justifyContent: 'center' }}>
        <button style={S.bottoneSecondario} onClick={() => vaiA('prezzo')}>Fascia di prezzo</button>
        <button style={S.bottonePrimario} onClick={onCheck}>Ho finito</button>
      </div>
    </div>
  );
}

/* ============================================================
   SEGMENTO TRASPORTO
   ============================================================ */
function Trasporto({ tratte, onChange, anagrafica, noleggioCoincidente, isMobile, vaiA, registraIndietro, indiceModifica }) {
  // in modifica il form si apre già compilato con la tratta scelta dalla panoramica
  const [bozza, setBozza] = useState(() => (indiceModifica != null ? { ...tratte[indiceModifica] } : null));
  const [mezziScelti, setMezziScelti] = useState([]);
  const [codaMezzi, setCodaMezzi] = useState([]);
  const c = CONFIG.colori.trasporto;

  const toggleMezzo = (id) => {
    setMezziScelti((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const nuovaTratta = (mezzo) => {
    const tipo = !tratte.some((t) => t.tipo === 'andata') ? 'andata'
      : !tratte.some((t) => t.tipo === 'ritorno') ? 'ritorno' : 'spostamento';
    setBozza({
      mezzo,
      ...percorsoDaAnagrafica(tipo, anagrafica),
      orario: '',
      tipo,
      prezzo: '',
      codice_volo: '',
      fascia_tariffa: '',
      classe_volo: '',
      volo_selezionato: '',
      numero_treno: '',
      compagnia_treno: '',
      classe_treno: '',
      // campi tratta su strada
      veicolo: '',
      alimentazione: '',
      prezzo_carburante: '',
      km: '',
      pedaggi: '',
      tappe: [],
      costo_stimato: '',
      preferenza_strada: CONFIG.preferenzeStrada[0],
      paese_viaggio: 'italia',
      targa: '',
    });
  };

  const salvaTratta = () => {
    if (indiceModifica != null) {
      onChange(tratte.map((t, j) => (j === indiceModifica ? bozza : t)));
      setBozza(null);
      vaiA('anteprima');
      return;
    }
    onChange([...tratte, bozza]);
    if (codaMezzi.length > 0) {
      const [prossimo, ...resto] = codaMezzi;
      setCodaMezzi(resto);
      nuovaTratta(prossimo);
    } else {
      setBozza(null);
      vaiA('anteprima');
    }
  };

  const annullaForm = () => {
    setCodaMezzi([]);
    setBozza(null);
    if (indiceModifica != null) vaiA('anteprima');
  };

  useEffect(() => {
    if (!registraIndietro) return undefined;
    registraIndietro(bozza ? annullaForm : null);
    return () => registraIndietro(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bozza]);

  return (
    <section style={{ ...S.sezione, maxWidth: 'min(1300px, 94vw)' }}>
      <IntestazioneSegmento colore={c} titolo="Trasporto"
        nota="Come ti sei spostato. Il costo del noleggio non va qui: quello è un servizio."
        iconaMezzo={bozza && <IconaMezzo mezzo={bozza.mezzo} colore={c} />} />

      {!bozza && (
        <div style={S.trasportoIconeRiga}>
          {MEZZI_PRINCIPALI.map((id) => {
            const attivo = mezziScelti.includes(id);
            const m = CONFIG.mezzi.find((x) => x.id === id);
            return (
              <button
                key={id}
                className="octa-mezzo-icona"
                style={{ ...S.trasportoIconaMini, opacity: mezziScelti.length === 0 || attivo ? 1 : 0.4 }}
                onClick={() => toggleMezzo(id)}
              >
                <svg viewBox="0 0 24 24" width="66" height="66" className="octa-mezzo-svg"
                  fill={attivo ? c : '#8c8c8c'}>
                  <path d={MEZZO_PATH[id]} />
                </svg>
                <span style={S.trasportoIconaLabel}>{m.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {noleggioCoincidente && !tratte.some((t) => t.da_noleggio) && (
        <div style={{ ...S.suggerimento, borderColor: c }}>
          Hai un noleggio che copre esattamente le date del viaggio. Vuoi usarlo come mezzo di trasporto?
          <button
            style={{ ...S.bottoneMini, background: c }}
            onClick={() =>
              onChange([
                ...tratte,
                {
                  mezzo: 'auto', da: anagrafica.origine, a: anagrafica.destinazione,
                  data: anagrafica.data_partenza, tipo: 'andata', da_noleggio: true,
                  veicolo: noleggioCoincidente.veicolo || '', prezzo: '', costo_stimato: '',
                },
              ])
            }
          >
            Sì, usalo
          </button>
        </div>
      )}

      {tratte.length > 0 && (
        <ol style={S.listaElementi}>
          {tratte.map((t, i) => (
            <li key={i} style={S.elemento}>
              <span style={S.elementoIcona}>{CONFIG.mezzi.find((m) => m.id === t.mezzo)?.icona}</span>
              <div style={S.elementoTesto}>
                <strong>{t.da || '—'} → {t.a || '—'}</strong>
                <span style={S.elementoMeta}>
                  {fmtData(t.data)} · {t.tipo}
                  {t.codice_volo ? ` · ${t.codice_volo}` : ''}
                  {t.km ? ` · ${t.km} km` : ''}
                </span>
              </div>
              <span style={S.elementoCosto}>
                {euro((parseFloat(t.prezzo) || 0) + (parseFloat(t.costo_stimato) || 0))}
              </span>
              <button style={S.rimuovi} onClick={() => onChange(tratte.filter((_, j) => j !== i))}>×</button>
            </li>
          ))}
        </ol>
      )}

      {!bozza && (
        <>
          <SceltaMezzo
            scelti={mezziScelti}
            onScegli={toggleMezzo}
            onProsegui={() => {
              if (mezziScelti.length === 0) return;
              const [primo, ...resto] = mezziScelti;
              setCodaMezzi(resto);
              nuovaTratta(primo);
              setMezziScelti([]);
            }}
          />
          {tratte.length > 0 && !tratte.some((t) => t.tipo === 'ritorno') && (
            <p style={S.avviso}>Manca ancora la tratta di ritorno.</p>
          )}
        </>
      )}

      {bozza && bozza.mezzo === 'aereo' && (
        <FormVolo
          bozza={bozza}
          setBozza={setBozza}
          anagrafica={anagrafica}
          onSalva={salvaTratta}
          onAnnulla={annullaForm}
          isMobile={isMobile}
        />
      )}

      {bozza && bozza.mezzo === 'treno' && (
        <FormTreno
          bozza={bozza}
          setBozza={setBozza}
          anagrafica={anagrafica}
          onSalva={salvaTratta}
          onAnnulla={annullaForm}
          isMobile={isMobile}
        />
      )}

      {bozza && CONFIG.mezziSuStrada.includes(bozza.mezzo) && (
        <FormAuto
          bozza={bozza}
          setBozza={setBozza}
          anagrafica={anagrafica}
          onSalva={salvaTratta}
          onAnnulla={annullaForm}
        />
      )}

      {bozza && bozza.mezzo === 'pubblici' && (
        <FormTratta
          bozza={bozza}
          setBozza={setBozza}
          anagrafica={anagrafica}
          onSalva={salvaTratta}
          onAnnulla={annullaForm}
          colore={c}
        />
      )}
    </section>
  );
}

const MEZZO_PATH = {
  aereo: 'M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2.5 1.8V22l3.5-1 3.5 1v-1.2L12 19v-5.5l8 2.5z',
  treno: 'M12 2C8 2 4 2.5 4 6v9.5A3.5 3.5 0 0 0 7.5 19L6 20.5v.5h12v-.5L16.5 19a3.5 3.5 0 0 0 3.5-3.5V6c0-3.5-3.58-4-8-4zM7.5 17A1.5 1.5 0 1 1 9 15.5 1.5 1.5 0 0 1 7.5 17zm3.5-7H6V6h5zm2 0V6h5v4zm4 7a1.5 1.5 0 1 1 1.5-1.5A1.5 1.5 0 0 1 17 17z',
  auto: 'M5 11l1.4-4.2A2 2 0 0 1 8.3 5.4h7.4a2 2 0 0 1 1.9 1.4L19 11h.3A1.7 1.7 0 0 1 21 12.7v3.6a1.7 1.7 0 0 1-1.7 1.7H19v1a1 1 0 0 1-1 1h-.8a1 1 0 0 1-1-1v-1H7.8v1a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-1h-.3A1.7 1.7 0 0 1 3 16.3v-3.6A1.7 1.7 0 0 1 4.7 11H5zm1.8 0h10.4l-1.1-3.3a.5.5 0 0 0-.5-.3H8.4a.5.5 0 0 0-.5.3L6.8 11zM6.4 14a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2zm11.2 0a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2z',
  pubblici: 'M5 5.5A1.5 1.5 0 0 1 6.5 4h11A1.5 1.5 0 0 1 19 5.5V15a1.5 1.5 0 0 1-1 1.41V18a1 1 0 0 1-1 1h-.5a1 1 0 0 1-1-1v-.5h-7V18a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-1.59A1.5 1.5 0 0 1 5 15V5.5zM6.5 13h11V6.5a.5.5 0 0 0-.5-.5h-10a.5.5 0 0 0-.5.5V13zm1 2a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm9 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2z',
};
// Partenza/arrivo/data della tratta ricavati dalle info di base: l'andata va da origine a destinazione, il ritorno al contrario.
function percorsoDaAnagrafica(tipo, anagrafica) {
  if (tipo === 'andata') return { da: anagrafica.origine, a: anagrafica.destinazione, data: anagrafica.data_partenza };
  if (tipo === 'ritorno') return { da: anagrafica.destinazione, a: anagrafica.origine, data: anagrafica.data_ritorno };
  return { da: '', a: '', data: '' };
}

// Cambiando andata/ritorno si aggiornano i campi ancora uguali a quelli precompilati; quelli modificati a mano restano.
function cambiaTipoTratta(bozza, tipo, anagrafica) {
  const prima = percorsoDaAnagrafica(bozza.tipo, anagrafica);
  const dopo = percorsoDaAnagrafica(tipo, anagrafica);
  const aggiorna = (k) => (!bozza[k] || bozza[k] === prima[k] ? dopo[k] : bozza[k]);
  return { ...bozza, tipo, da: aggiorna('da'), a: aggiorna('a'), data: aggiorna('data') };
}

const MEZZI_PRINCIPALI = ['aereo', 'treno', 'auto', 'pubblici'];
const TIPO_TRATTA_LABEL = { andata: 'Andata', ritorno: 'Ritorno', spostamento: 'Spostamento interno' };
const MEZZI_SECONDARI = ['moto', 'bicicletta'];

function IconaMezzo({ mezzo, colore, size = 26 }) {
  if (!MEZZO_PATH[mezzo]) {
    const m = CONFIG.mezzi.find((x) => x.id === mezzo);
    return <span style={{ fontSize: size, lineHeight: 1, flexShrink: 0 }}>{m?.icona}</span>;
  }
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill={colore} style={{ flexShrink: 0 }}>
      <path d={MEZZO_PATH[mezzo]} />
    </svg>
  );
}

function IconaAlloggio({ tipo, colore, size = 26 }) {
  const icona = CONFIG.tipiAlloggio.find((t) => t.id === tipo)?.icona;
  if (!icona) return null;
  return (
    <span
      role="img"
      aria-label=""
      style={{
        display: 'inline-block', width: size, height: size, flexShrink: 0, backgroundColor: colore,
        WebkitMaskImage: `url(${icona})`, maskImage: `url(${icona})`,
        WebkitMaskSize: 'contain', maskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center', maskPosition: 'center',
      }}
    />
  );
}
const FILTRO_ROSSO = 'brightness(0) saturate(100%) invert(21%) sepia(94%) saturate(7472%) hue-rotate(356deg) brightness(94%) contrast(96%)';

function SceltaMezzo({ scelti, onScegli, onProsegui }) {
  const [altroAperto, setAltroAperto] = useState(false);

  return (
    <div style={S.sceltaMezzo}>
      {!altroAperto ? (
        <button style={S.linkPiccolo} onClick={() => setAltroAperto(true)}>Altro mezzo (moto, bicicletta)</button>
      ) : (
        <div style={S.sceltaMezzoRigaSecondaria}>
          {MEZZI_SECONDARI.map((id) => {
            const m = CONFIG.mezzi.find((x) => x.id === id);
            const attivo = scelti.includes(id);
            return (
              <button
                key={id}
                className="octa-mezzo-icona"
                style={{ ...S.sceltaMezzoIcona, opacity: scelti.length === 0 || attivo ? 1 : 0.4 }}
                onClick={() => onScegli(id)}
              >
                <div style={S.sceltaMezzoIconaBox}>
                  <span style={{ ...S.iconaSecondariaGrande, filter: attivo ? FILTRO_ROSSO : 'none' }} className="octa-mezzo-emoji">{m.icona}</span>
                </div>
                <span style={S.iconaPlaceholderLabel}>{m.label}</span>
              </button>
            );
          })}
        </div>
      )}

      <div>
        <button style={{ ...S.bottoneProsegui, opacity: scelti.length > 0 ? 1 : 0.4 }} disabled={scelti.length === 0} onClick={onProsegui}>
          PROSEGUI
        </button>
      </div>
    </div>
  );
}

function FormTratta({ bozza, setBozza, anagrafica, onSalva, onAnnulla, colore }) {
  const set = (k, v) => setBozza({ ...bozza, [k]: v });

  return (
    <div style={{ ...S.form, borderColor: colore }}>
      <div style={S.griglia2}>
        <Campo label="Da">
          <input style={S.input} value={bozza.da} onChange={(e) => set('da', e.target.value)} placeholder="Milano" />
        </Campo>
        <Campo label="A">
          <input style={S.input} value={bozza.a} onChange={(e) => set('a', e.target.value)} placeholder="Napoli" />
        </Campo>
        <Campo label="Data">
          <input type="date" style={S.input} value={bozza.data}
            min={anagrafica.data_partenza} max={anagrafica.data_ritorno}
            onChange={(e) => set('data', e.target.value)} />
        </Campo>
        <Campo label="Tipo di tratta">
          <select style={S.input} value={bozza.tipo} onChange={(e) => setBozza(cambiaTipoTratta(bozza, e.target.value, anagrafica))}>
            <option value="andata">Andata</option>
            <option value="ritorno">Ritorno</option>
            <option value="spostamento">Spostamento interno</option>
          </select>
        </Campo>

        <Campo label="Prezzo del biglietto (facoltativo)">
          <input type="number" style={S.input} value={bozza.prezzo} onChange={(e) => set('prezzo', e.target.value)} placeholder="€" />
        </Campo>
      </div>

      <div style={S.rigaBottoni}>
        <button style={S.bottoneSecondario} onClick={onAnnulla}>Annulla</button>
        <button style={{ ...S.bottonePrimario, background: colore }} onClick={onSalva}>Aggiungi la tratta</button>
      </div>
    </div>
  );
}

function IconaLente() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" style={S.lenteVolo} fill={CONFIG.colori.testoSoft}>
      <path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 1 0-.7.7l.27.28v.79l5 5L20.49 19l-5-5zm-6 0A4.5 4.5 0 1 1 14 9.5 4.5 4.5 0 0 1 9.5 14z" />
    </svg>
  );
}

function ListaRisultati({ risultati, selezionato, onSeleziona }) {
  return (
    <div style={S.risultatoTreno}>
      <div style={S.risultatoTrenoHeader}>
        <span style={{ width: 100 }}>Orari</span>
        <span style={{ width: 50 }}>Durata</span>
        <span style={{ width: 80 }}>Info Viaggio</span>
      </div>
      <div style={S.risultatoTrenoScroll}>
        {risultati.map((r) => {
          const attivo = selezionato === r.id;
          return (
            <div key={r.id} style={{ ...S.risultatoTrenoRiga, ...(attivo ? S.risultatoTrenoRigaAttiva : {}) }}>
              <span style={{ width: 100 }}>{r.orario}</span>
              <span style={{ width: 50 }}>{r.durata}</span>
              <span style={{ width: 80, display: 'flex', justifyContent: 'center' }}>
                <span style={S.infoIcona}>i</span>
              </span>
              <button
                style={{ ...S.bottoneProseguiPiccolo, ...(attivo ? S.bottoneProseguiPiccoloAttivo : {}) }}
                onClick={() => onSeleziona(attivo ? null : r.id)}
              >
                SELEZIONA
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RigaSpacerProsegui({ mostrato, onProsegui }) {
  return (
    <div style={S.rigaSpacerProsegui}>
      <span style={{ width: 100 }} />
      <span style={{ width: 50 }} />
      <span style={{ width: 80 }} />
      {mostrato && <button style={S.bottoneProseguiPiccolo} onClick={onProsegui}>PROSEGUI</button>}
    </div>
  );
}

function SelettoreTipoTratta({ valore, onScegli, colore }) {
  return (
    <div style={S.rigaTipoTratta}>
      <span style={S.labelVolo}>Questa tratta è di</span>
      <div style={{ ...S.pillMezziAuto, justifyContent: 'flex-start', marginBottom: 0 }}>
        {['andata', 'ritorno'].map((id) => (
          <button
            key={id}
            style={{
              ...S.pillMezzoAuto, padding: '4px 14px', fontSize: 12,
              ...(valore === id ? { ...S.pillMezzoAutoAttivo, background: colore, borderColor: colore } : {}),
            }}
            onClick={() => onScegli(id)}
          >
            {TIPO_TRATTA_LABEL[id]}
          </button>
        ))}
      </div>
    </div>
  );
}

function FormVolo({ bozza, setBozza, anagrafica, onSalva, onAnnulla, isMobile }) {
  const set = (k, v) => setBozza({ ...bozza, [k]: v });
  const [selezionato, setSelezionato] = useState(null);
  const [risultatiVisibili, setRisultatiVisibili] = useState(false);
  const campiCompleti = Boolean(bozza.da && bozza.a && bozza.fascia_tariffa);

  // i risultati si azzerano solo quando cambiano da/a/tariffa; finché restano quelli di apertura
  // (es. tratta aperta con "modifica") l'orario già scelto resta selezionato.
  // Confronto sui valori e non "primo render": con React.StrictMode l'effetto in sviluppo gira due volte.
  const percorsoIniziale = useRef([bozza.da, bozza.a, bozza.fascia_tariffa].join('|'));
  useEffect(() => {
    if ([bozza.da, bozza.a, bozza.fascia_tariffa].join('|') === percorsoIniziale.current) {
      const scelto = risultati.find((r) => r.orario === bozza.orario);
      if (scelto) { setRisultatiVisibili(true); setSelezionato(scelto.id); }
      return;
    }
    percorsoIniziale.current = null;
    setRisultatiVisibili(false);
    setSelezionato(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bozza.da, bozza.a, bozza.fascia_tariffa]);

  // Dati fittizi: verranno sostituiti dai risultati reali dell'API voli.
  const risultati = [
    { id: 1, orario: '06:15 – 08:00', durata: '1h 45m' },
    { id: 2, orario: '07:20 – 09:05', durata: '1h 45m' },
    { id: 3, orario: '09:10 – 10:55', durata: '1h 45m' },
    { id: 4, orario: '11:40 – 13:30', durata: '1h 50m' },
    { id: 5, orario: '13:25 – 15:10', durata: '1h 45m' },
    { id: 6, orario: '15:50 – 17:40', durata: '1h 50m' },
    { id: 7, orario: '18:05 – 19:55', durata: '1h 50m' },
    { id: 8, orario: '20:30 – 22:15', durata: '1h 45m' },
  ];

  return (
    <div style={S.formVolo}>
      <SelettoreTipoTratta valore={bozza.tipo} onScegli={(v) => setBozza(cambiaTipoTratta(bozza, v, anagrafica))} colore={CONFIG.colori.trasporto} />
      <div style={isMobile ? S.formVoloGridMobile : S.formVoloGrid}>
        <div style={isMobile ? S.rigaVoloGruppoSxMobile : { ...S.rigaVoloGruppoSx, gridColumn: 1, gridRow: 1 }}>
          <div style={{ ...S.campoVolo, alignItems: 'flex-start', ...(isMobile ? { flex: 'none', minWidth: 0, width: '100%' } : {}) }}>
            <span style={S.labelVolo}>Da</span>
            <div style={S.inputVoloWrap}>
              <input className="octa-input-citta" style={{ ...S.inputVolo, textAlign: 'left' }} value={bozza.da} onChange={(e) => set('da', e.target.value)}
                placeholder={anagrafica.origine || 'Partenza'} />
              <IconaLente />
            </div>
            <span style={S.sottoVolo}>Aereoporto di partenza</span>
            {/* ⚠️ INNESTO 5 — autocomplete su tabella aeroporti IATA (Supabase), filtrata sulla città dell'anagrafica */}
          </div>

          <div style={{ ...S.campoVolo, alignItems: 'flex-start', ...(isMobile ? { flex: 'none', minWidth: 0, width: '100%' } : {}) }}>
            <span style={S.labelVolo}>A</span>
            <div style={S.inputVoloWrap}>
              <input className="octa-input-citta" style={{ ...S.inputVolo, textAlign: 'left' }} value={bozza.a} onChange={(e) => set('a', e.target.value)}
                placeholder={anagrafica.destinazione || 'Arrivo'} />
              <IconaLente />
            </div>
            <span style={S.sottoVolo}>Aereoporto di arrivo</span>
            <div style={{ ...S.rigaSottoA, justifyContent: 'flex-start' }}>
              <select style={S.pillTariffa} value={bozza.fascia_tariffa} onChange={(e) => set('fascia_tariffa', e.target.value)}>
                <option value="">LA TUA TARIFFA ▾</option>
                {CONFIG.fasceTariffa.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
              <select style={S.pillTariffa} value={bozza.classe_volo} onChange={(e) => set('classe_volo', e.target.value)}>
                <option value="">LA TUA CLASSE ▾</option>
                {CONFIG.classiVolo.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
        </div>

        {!isMobile && <div style={{ ...S.divisoreVoloGrid, gridColumn: 2, gridRow: '1 / 3' }} />}

        <div style={isMobile ? {} : { minWidth: 300, gridColumn: 3, gridRow: 1 }}>
          {risultatiVisibili ? (
            <ListaRisultati risultati={risultati} selezionato={selezionato} onSeleziona={(id) => {
              // orari e durata del risultato scelto restano nella tratta (servono alla card della panoramica)
              const r = risultati.find((x) => x.id === id);
              setSelezionato(id);
              setBozza({ ...bozza, orario: r?.orario || '', durata: r?.durata || '' });
            }} />
          ) : (
            <button
              style={{ ...S.bottoneProsegui, opacity: campiCompleti ? 1 : 0.4, marginTop: 0 }}
              disabled={!campiCompleti}
              onClick={() => setRisultatiVisibili(true)}
            >
              Seleziona risultato
            </button>
          )}
          {/* ⚠️ INNESTO 7 — elenco voli reali sulla tratta/data via API (es. AviationStack) */}
        </div>

        <div style={isMobile ? {} : { minWidth: 320, gridColumn: 1, gridRow: 2 }}>
          <p style={{ ...S.etichettaGruppo, fontWeight: 400 }}>Se ti ricordi, inserisci direttamente il codice del volo</p>
          <div style={{ ...S.inputVoloWrap, maxWidth: 320 }}>
            <input className="octa-input-citta" style={S.inputVolo} value={bozza.codice_volo} onChange={(e) => set('codice_volo', e.target.value)} placeholder="FR 4521" />
            <IconaLente />
          </div>
          {/* ⚠️ INNESTO 8 — dal codice volo, l'API compila automaticamente da/a/orari/tariffa */}
        </div>

        <div style={isMobile ? {} : { gridColumn: 3, gridRow: 2 }}>
          <RigaSpacerProsegui mostrato={selezionato !== null} onProsegui={onSalva} />
        </div>
      </div>

      <div style={S.rigaBottoni}>
        <button style={S.bottoneSecondario} onClick={onAnnulla}>Annulla</button>
      </div>
    </div>
  );
}

function FormTreno({ bozza, setBozza, anagrafica, onSalva, onAnnulla, isMobile }) {
  const set = (k, v) => setBozza({ ...bozza, [k]: v });
  const [selezionato, setSelezionato] = useState(null);
  const [risultatiVisibili, setRisultatiVisibili] = useState(false);
  const campiCompleti = Boolean(bozza.da && bozza.a && bozza.fascia_tariffa);

  // i risultati si azzerano solo quando cambiano da/a/tariffa; finché restano quelli di apertura
  // (es. tratta aperta con "modifica") l'orario già scelto resta selezionato.
  // Confronto sui valori e non "primo render": con React.StrictMode l'effetto in sviluppo gira due volte.
  const percorsoIniziale = useRef([bozza.da, bozza.a, bozza.fascia_tariffa].join('|'));
  useEffect(() => {
    if ([bozza.da, bozza.a, bozza.fascia_tariffa].join('|') === percorsoIniziale.current) {
      const scelto = risultati.find((r) => r.orario === bozza.orario);
      if (scelto) { setRisultatiVisibili(true); setSelezionato(scelto.id); }
      return;
    }
    percorsoIniziale.current = null;
    setRisultatiVisibili(false);
    setSelezionato(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bozza.da, bozza.a, bozza.fascia_tariffa]);

  // Dati fittizi: verranno sostituiti dai risultati reali dell'API orari treni.
  const risultati = [
    { id: 1, orario: '05:50 – 08:55', durata: '3h 5m' },
    { id: 2, orario: '07:22 – 09:05', durata: '3h' },
    { id: 3, orario: '08:40 – 11:50', durata: '3h 10m' },
    { id: 4, orario: '10:15 – 13:40', durata: '3h 25m' },
    { id: 5, orario: '12:30 – 15:35', durata: '3h 5m' },
    { id: 6, orario: '15:30 – 18:50', durata: '3h 20m' },
    { id: 7, orario: '17:45 – 21:00', durata: '3h 15m' },
    { id: 8, orario: '19:20 – 22:30', durata: '3h 10m' },
  ];

  return (
    <div style={S.formVolo}>
      <SelettoreTipoTratta valore={bozza.tipo} onScegli={(v) => setBozza(cambiaTipoTratta(bozza, v, anagrafica))} colore={CONFIG.colori.trasporto} />
      <div style={isMobile ? S.formVoloGridMobile : S.formVoloGrid}>
        <div style={isMobile ? S.rigaVoloGruppoSxMobile : { ...S.rigaVoloGruppoSx, gridColumn: 1, gridRow: 1 }}>
          <div style={{ ...S.campoVolo, alignItems: 'flex-start', ...(isMobile ? { flex: 'none', minWidth: 0, width: '100%' } : {}) }}>
            <span style={S.labelVolo}>Da</span>
            <div style={S.inputVoloWrap}>
              <input className="octa-input-citta" style={{ ...S.inputVolo, textAlign: 'left' }} value={bozza.da} onChange={(e) => set('da', e.target.value)}
                placeholder={anagrafica.origine || 'Città di partenza'} />
              <IconaLente />
            </div>
            <span style={S.sottoVolo}>Stazione di partenza</span>
            <div style={{ ...S.rigaSottoA, justifyContent: 'flex-start', flexWrap: 'nowrap', overflowX: 'visible', maxWidth: 'none', width: 'max-content' }}>
              <select style={S.pillTariffa} value={bozza.fascia_tariffa} onChange={(e) => set('fascia_tariffa', e.target.value)}>
                <option value="">LA TUA TARIFFA ▾</option>
                {CONFIG.fasceTariffa.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
              <select
                style={S.pillTariffa}
                value={bozza.compagnia_treno}
                onChange={(e) => setBozza({ ...bozza, compagnia_treno: e.target.value, classe_treno: '' })}
              >
                <option value="">LA TUA COMPAGNIA ▾</option>
                {CONFIG.compagnieTreno.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
              <select
                style={S.pillTariffa}
                value={bozza.classe_treno}
                disabled={!bozza.compagnia_treno}
                onChange={(e) => set('classe_treno', e.target.value)}
              >
                <option value="">LA TUA CLASSE ▾</option>
                {(CONFIG.classiTreno[bozza.compagnia_treno] || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {/* ⚠️ INNESTO 5 — autocomplete su tabella stazioni, filtrata sulla città dell'anagrafica */}
          </div>

          <div style={{ ...S.campoVolo, alignItems: 'flex-start', ...(isMobile ? { flex: 'none', minWidth: 0, width: '100%' } : {}) }}>
            <span style={S.labelVolo}>A</span>
            <div style={S.inputVoloWrap}>
              <input className="octa-input-citta" style={{ ...S.inputVolo, textAlign: 'left' }} value={bozza.a} onChange={(e) => set('a', e.target.value)}
                placeholder={anagrafica.destinazione || 'Città di arrivo'} />
              <IconaLente />
            </div>
            <span style={S.sottoVolo}>Stazione di arrivo</span>
          </div>
        </div>

        {!isMobile && <div style={{ ...S.divisoreVoloGrid, gridColumn: 2, gridRow: '1 / 3' }} />}

        <div style={isMobile ? {} : { minWidth: 300, gridColumn: 3, gridRow: 1 }}>
          {risultatiVisibili ? (
            <ListaRisultati risultati={risultati} selezionato={selezionato} onSeleziona={(id) => {
              // orari e durata del risultato scelto restano nella tratta (servono alla card della panoramica)
              const r = risultati.find((x) => x.id === id);
              setSelezionato(id);
              setBozza({ ...bozza, orario: r?.orario || '', durata: r?.durata || '' });
            }} />
          ) : (
            <button
              style={{ ...S.bottoneProsegui, opacity: campiCompleti ? 1 : 0.4, marginTop: 0 }}
              disabled={!campiCompleti}
              onClick={() => setRisultatiVisibili(true)}
            >
              Seleziona risultato
            </button>
          )}
          {/* ⚠️ INNESTO 12 — orari/durata reali via API treni (es. Trenitalia/Italo) */}
        </div>

        <div style={isMobile ? {} : { minWidth: 320, gridColumn: 1, gridRow: 2 }}>
          <p style={S.etichettaGruppo}>Se ti ricordi, inserisci direttamente il numero del treno</p>
          <div style={{ ...S.inputVoloWrap, maxWidth: 320 }}>
            <input style={S.inputVolo} value={bozza.numero_treno} onChange={(e) => set('numero_treno', e.target.value)} placeholder="FR 9595" />
            <IconaLente />
          </div>
          {/* ⚠️ INNESTO 13 — dal numero treno, l'API compila automaticamente da/a/orari */}
        </div>

        <div style={isMobile ? {} : { gridColumn: 3, gridRow: 2 }}>
          <RigaSpacerProsegui mostrato={selezionato !== null} onProsegui={onSalva} />
        </div>
      </div>

      <div style={S.rigaBottoni}>
        <button style={S.bottoneSecondario} onClick={onAnnulla}>Annulla</button>
      </div>
    </div>
  );
}

function TargaInput({ valore, onChange }) {
  const caratteri = Array.from({ length: 10 }, (_, i) => (valore || '')[i] || '');

  const set = (i, ch) => {
    const arr = caratteri.slice();
    arr[i] = ch.slice(-1).toUpperCase();
    onChange(arr.join('').trimEnd());
  };

  return (
    <div style={S.rigaTarga}>
      {caratteri.map((ch, i) => (
        <input
          key={i}
          maxLength={1}
          style={S.boxTarga}
          value={ch}
          onChange={(e) => {
            set(i, e.target.value);
            if (e.target.value && e.target.nextElementSibling) e.target.nextElementSibling.focus();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !ch && e.target.previousElementSibling) e.target.previousElementSibling.focus();
          }}
        />
      ))}
    </div>
  );
}

function FormAuto({ bozza, setBozza, anagrafica, onSalva, onAnnulla }) {
  const set = (k, v) => setBozza({ ...bozza, [k]: v });
  const veicolo = CONFIG.veicoliStandard.find((v) => v.id === bozza.veicolo);
  const veicoliMostrati = CONFIG.veicoliStandard.filter((v) => v.tipoMezzo === (bozza.mezzo === 'moto' ? 'moto' : 'auto'));
  const tappe = useMemo(() => bozza.tappe || [], [bozza.tappe]);

  const sceglialimentazione = (id) =>
    setBozza({
      ...bozza,
      alimentazione: id,
      prezzo_carburante: String(CONFIG.prezziCarburante[bozza.paese_viaggio || 'italia']?.[id] ?? ''),
    });

  const scegliPaese = (id) =>
    setBozza({
      ...bozza,
      paese_viaggio: id,
      prezzo_carburante: bozza.alimentazione
        ? String(CONFIG.prezziCarburante[id]?.[bozza.alimentazione] ?? bozza.prezzo_carburante)
        : bozza.prezzo_carburante,
    });

  const aggiungiTappa = () => set('tappe', [...tappe, { nome: '', km: '', pedaggi: '' }]);
  const aggiornaTappa = (i, campo, valore) => set('tappe', tappe.map((t, j) => (j === i ? { ...t, [campo]: valore } : t)));
  const rimuoviTappa = (i) => set('tappe', tappe.filter((_, j) => j !== i));

  // ⚠️ INNESTO — in futuro pedaggi e prezzo carburante compilati in automatico via API (routing + prezzi carburante).
  const stima = useMemo(() => {
    if (!veicolo || !bozza.prezzo_carburante) return null;
    const prezzo = Number(bozza.prezzo_carburante);
    let consumo = 0;
    let carburante = 0;
    let pedaggi = 0;
    let tratteConKm = 0;

    tappe.forEach((t) => {
      if (t.km) {
        const c = (Number(t.km) * veicolo.consumo) / 100;
        consumo += c;
        carburante += c * prezzo;
        tratteConKm += 1;
      }
      pedaggi += Number(t.pedaggi) || 0;
    });

    if (bozza.km) {
      const c = (Number(bozza.km) * veicolo.consumo) / 100;
      consumo += c;
      carburante += c * prezzo;
      tratteConKm += 1;
    }
    pedaggi += Number(bozza.pedaggi) || 0;

    if (tratteConKm === 0) return null;
    return { consumo, carburante, pedaggi, totale: carburante + pedaggi, numTratte: tappe.length + 1 };
  }, [veicolo, bozza.km, bozza.pedaggi, bozza.prezzo_carburante, tappe]);

  const applicaStima = () => stima && set('costo_stimato', stima.totale.toFixed(2));

  return (
    <div style={S.formVolo}>
      <SelettoreTipoTratta valore={bozza.tipo} onScegli={(v) => setBozza(cambiaTipoTratta(bozza, v, anagrafica))} colore={CONFIG.colori.trasporto} />
      <div style={S.rigaVolo}>
        <div style={S.campoVolo}>
          <span style={S.labelVolo}>Da</span>
          <div style={S.inputVoloWrap}>
            <input style={S.inputVolo} value={bozza.da} onChange={(e) => set('da', e.target.value)}
              placeholder={anagrafica.origine || 'Punto di partenza'} />
            <IconaLente />
          </div>
        </div>

        <div style={S.campoVolo}>
          <span style={S.labelVolo}>A</span>
          <div style={S.inputVoloWrap}>
            <input style={S.inputVolo} value={bozza.a} onChange={(e) => set('a', e.target.value)}
              placeholder={anagrafica.destinazione || 'Punto di arrivo'} />
            <IconaLente />
          </div>
        </div>

        <button style={S.bottoneCerca} onClick={() => {}}>CERCA</button>
        {/* ⚠️ INNESTO 10 — calcolo itinerario e km reali via API mappe (es. ViaMichelin, OpenRouteService) */}
      </div>

      <div>
        {tappe.map((t, i) => (
          <div key={i} style={S.rigaVolo}>
            <div style={S.campoVolo}>
              <span style={S.labelVolo}>Tappa {i + 1}</span>
              <div style={S.inputVoloWrap}>
                <input style={S.inputVolo} value={t.nome} onChange={(e) => aggiornaTappa(i, 'nome', e.target.value)}
                  placeholder="Città intermedia" />
                <IconaLente />
              </div>
            </div>
            <Campo label="Km fino a qui">
              <input type="number" style={S.input} value={t.km} onChange={(e) => aggiornaTappa(i, 'km', e.target.value)} placeholder="km" />
            </Campo>
            <Campo label="Pedaggi fino a qui">
              <input type="number" style={S.input} value={t.pedaggi} onChange={(e) => aggiornaTappa(i, 'pedaggi', e.target.value)} placeholder="€" />
            </Campo>
            <button style={S.rimuovi} onClick={() => rimuoviTappa(i)}>×</button>
          </div>
        ))}
        <button style={S.linkPiccolo} onClick={aggiungiTappa}>+ Aggiungi tappa intermedia</button>
      </div>

      <div style={S.duColonneAuto}>
        <div>
          <p style={S.etichettaGruppo}>Le tue preferenze su strada</p>
          <select style={S.input} value={bozza.preferenza_strada} onChange={(e) => set('preferenza_strada', e.target.value)}>
            {CONFIG.preferenzeStrada.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>

          <p style={S.etichettaGruppo}>Alimentazione</p>
          <div style={S.pillMezziAuto}>
            {CONFIG.tipiAlimentazione.map((f) => (
              <button
                key={f.id}
                style={{ ...S.pillMezzoAuto, ...(bozza.alimentazione === f.id ? S.pillMezzoAutoAttivo : {}) }}
                onClick={() => sceglialimentazione(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>

          <Campo label={bozza.alimentazione === 'elettrico' ? '€ al kWh' : '€ al litro'}>
            <input type="number" step="0.01" style={S.input} value={bozza.prezzo_carburante}
              onChange={(e) => set('prezzo_carburante', e.target.value)} placeholder="1.85" />
          </Campo>

          <Campo label={tappe.length > 0 ? "Km dell'ultima tratta" : 'Km percorsi'}>
            <input type="number" style={S.input} value={bozza.km} onChange={(e) => set('km', e.target.value)} placeholder="km" />
          </Campo>

          <Campo label={tappe.length > 0 ? "Pedaggi dell'ultima tratta, se te li ricordi" : 'Pedaggi, se te li ricordi'}>
            <input type="number" style={S.input} value={bozza.pedaggi} onChange={(e) => set('pedaggi', e.target.value)} placeholder="€" />
          </Campo>

          <Campo label="Paese in cui viaggi">
            <select style={S.input} value={bozza.paese_viaggio} onChange={(e) => scegliPaese(e.target.value)}>
              {CONFIG.paesiViaggio.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </Campo>
        </div>

        <div>
          <p style={S.etichettaGruppo}>Il tuo mezzo di trasporto</p>
          <div style={S.pillMezziAuto}>
            {CONFIG.mezziSuStrada.map((id) => {
              const m = CONFIG.mezzi.find((x) => x.id === id);
              const attivo = bozza.mezzo === id;
              return (
                <button key={id} style={{ ...S.pillMezzoAuto, ...(attivo ? S.pillMezzoAutoAttivo : {}) }} onClick={() => set('mezzo', id)}>
                  {m.label}
                </button>
              );
            })}
          </div>

          {bozza.mezzo !== 'bicicletta' && (
            <>
              <p style={S.targaCaption}>Calcola l'itinerario inserendo la targa del tuo veicolo</p>
              <TargaInput valore={bozza.targa} onChange={(v) => set('targa', v)} />
              {/* ⚠️ INNESTO 11 — lookup veicolo/consumi da targa via API */}

              <p style={S.etichettaGruppo}>Scegli un veicolo standard</p>
              <div style={S.grigliaVeicoli}>
                {veicoliMostrati.map((v) => (
                  <button
                    key={v.id}
                    style={{ ...S.cardVeicolo, ...(bozza.veicolo === v.id ? S.cardVeicoloAttivo : {}) }}
                    onClick={() => set('veicolo', v.id)}
                  >
                    <strong style={S.cardVeicoloNome}>{v.nome.toUpperCase()}</strong>
                    <span style={S.cardVeicoloCarburante}>{v.carburante}</span>
                    <span style={S.cardVeicoloTag}>{v.categoria}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {stima && (
        <div style={S.riquadroStima}>
          <div>Consumo stimato: <strong>{stima.consumo.toFixed(1)} {veicolo.elettrico ? 'kWh' : 'litri'}</strong></div>
          <div>Carburante: <strong>{euro(stima.carburante)}</strong> · Pedaggi: <strong>{euro(stima.pedaggi)}</strong></div>
          {stima.numTratte > 1 && (
            <div style={{ fontSize: 12, color: CONFIG.colori.testoSoft }}>Calcolato su {stima.numTratte} tratte</div>
          )}
          <div style={{ marginTop: 6 }}>Totale spostamento: <strong>{euro(stima.totale)}</strong></div>
          <button style={{ ...S.bottoneMini, background: CONFIG.colori.trasporto }} onClick={applicaStima}>Usa questa stima</button>
        </div>
      )}
      <p style={S.nota}>
        Il canone di noleggio, se il mezzo era noleggiato, va nei Servizi. Qui contano solo carburante e pedaggi.
      </p>

      <div style={S.rigaBottoni}>
        <button style={S.bottoneSecondario} onClick={onAnnulla}>Annulla</button>
        <button style={{ ...S.bottonePrimario, background: CONFIG.colori.trasporto }} onClick={onSalva}>Aggiungi la tratta</button>
      </div>
    </div>
  );
}

/* ============================================================
   SEGMENTO ALLOGGIO
   ============================================================ */
/* Risultato della ricerca per nome (riferimento: public/images/alloggio/risultato.png).
   ⚠️ INNESTO 7 — con Google Places arriveranno foto, stelle, valutazione, descrizione e servizi offerti:
   finché non ci sono, la card mostra solo ciò che l'utente ha inserito (nessun dato inventato). */
function RisultatoAlloggio({ bozza, colore, selezionato, onSeleziona }) {
  const notti = nottiTra(bozza.date_da, bozza.date_a).length;
  return (
    <div style={{ ...S.risultatoAlloggio, borderColor: selezionato ? colore : CONFIG.colori.bordo }}>
      <strong style={{ ...S.risultatoAlloggioNome, color: CONFIG.colori.dark }}>{bozza.nome}</strong>
      <div style={{ ...S.risultatoAlloggioFoto, background: `${colore}14` }}>
        <IconaAlloggio tipo={bozza.tipo} colore={colore} size={56} />
      </div>
      <div style={S.risultatoAlloggioBarra}>
        <span style={S.risultatoAlloggioDate}>
          {fmtData(bozza.date_da)} – {fmtData(bozza.date_a)}
          <span style={{ display: 'block', fontSize: 12, color: CONFIG.colori.testoSoft }}>{notti} notti</span>
        </span>
        <button style={{ ...S.risultatoAlloggioSeleziona, background: colore, opacity: selezionato ? 0.85 : 1 }} onClick={onSeleziona}>
          {selezionato ? '✓ SELEZIONATO' : 'SELEZIONA'}
        </button>
      </div>
      {bozza.fascia_prezzo && (
        <div style={S.risultatoAlloggioPrezzo}>
          <strong style={{ fontSize: 22, color: CONFIG.colori.dark }}>{bozza.fascia_prezzo}</strong>
          <span style={{ fontSize: 12, color: CONFIG.colori.testoSoft }}> a notte</span>
        </div>
      )}
    </div>
  );
}

function ListaRisultatiAlloggio({ risultati, selezionato, onSeleziona, colore, intestazione = 'Struttura', colonnaPrezzo = 'A notte' }) {
  return (
    <div style={S.risultatoTreno}>
      <div style={S.risultatoTrenoHeader}>
        <span style={{ width: 170, textAlign: 'left' }}>{intestazione}</span>
        <span style={{ width: 70 }}>{colonnaPrezzo}</span>
      </div>
      <div style={S.risultatoTrenoScroll}>
        {risultati.map((r) => {
          const attivo = selezionato === r.id;
          return (
            <div key={r.id} style={{ ...S.risultatoTrenoRiga, ...(attivo ? { background: `${colore}12` } : {}) }}>
              <span style={{ width: 170, textAlign: 'left' }}>
                <strong style={{ display: 'block' }}>{r.nome}</strong>
                <span style={{ fontSize: 11, color: CONFIG.colori.testoSoft }}>{r.info}</span>
              </span>
              <span style={{ width: 70 }}>{euro(r.prezzo)}</span>
              <button
                style={{
                  ...S.bottoneProseguiPiccolo, borderColor: colore, color: attivo ? '#fff' : colore,
                  ...(attivo ? { background: colore } : {}),
                }}
                onClick={() => onSeleziona(r)}
              >
                SELEZIONA
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ModaleCriteriAlloggio({ colore, criteri, onCriterio, filtriNascosti, onNascondi, cerca, onCerca, onChiudi, onProsegui }) {
  const colonne = [
    { chiave: 'posizione', titolo: 'Posizione' },
    { chiave: 'recensioni', titolo: 'Recensioni' },
    { chiave: 'comfort', titolo: 'Comfort' },
    { chiave: 'tipologia', titolo: 'Tipologia' },
    { chiave: 'servizi', titolo: 'Servizi' },
  ];
  const q = cerca.trim().toLowerCase();
  const [colonneAperte, setColonneAperte] = useState({
    prezzo: true, posizione: true, recensioni: true, comfort: true, tipologia: true, servizi: true,
  });
  const toggleColonna = (chiave) => setColonneAperte((prev) => ({ ...prev, [chiave]: !prev[chiave] }));

  return (
    <div style={S.modaleOverlay} onClick={onChiudi}>
      <div style={S.modaleBox} onClick={(e) => e.stopPropagation()}>
        <button style={S.modaleChiudi} onClick={onChiudi} aria-label="Chiudi">×</button>
        <h2 style={{ ...S.h2, color: colore, textAlign: 'center', textTransform: 'uppercase' }}>
          Seleziona i criteri che hai utilizzato
        </h2>

        <div style={S.modaleRigaSopra}>
          <label style={{ ...S.checkRiga, margin: 0 }}>
            <input type="checkbox" style={S.checkboxGrande} checked={filtriNascosti} onChange={(e) => onNascondi(e.target.checked)} />
            Nascondi i filtri
          </label>
          <div style={{ ...S.inputVoloWrap, maxWidth: 240 }}>
            <input style={S.inputVolo} value={cerca} onChange={(e) => onCerca(e.target.value)} placeholder="Cerca" />
            <IconaLente />
          </div>
        </div>

        {!filtriNascosti && (
          <div style={S.modaleColonneScroll}>
            <div style={S.modaleColonne}>
              <div style={S.modaleColonna}>
                <button style={S.modalePillTitolo} onClick={() => toggleColonna('prezzo')}>
                  Prezzo {colonneAperte.prezzo ? '▾' : '▸'}
                </button>
                {colonneAperte.prezzo && (
                  <>
                    <Campo label="Da">
                      <input type="number" style={S.inputPrezzoModale} value={criteri.prezzo_da}
                        onChange={(e) => onCriterio('prezzo_da', e.target.value, true)} placeholder="€" />
                    </Campo>
                    <Campo label="A">
                      <input type="number" style={S.inputPrezzoModale} value={criteri.prezzo_a}
                        onChange={(e) => onCriterio('prezzo_a', e.target.value, true)} placeholder="€" />
                    </Campo>
                  </>
                )}
              </div>

              {colonne.map((col) => {
                const voci = CONFIG.criteriAlloggio[col.chiave].filter((v) => !q || v.toLowerCase().includes(q));
                const aperta = colonneAperte[col.chiave];
                return (
                  <div key={col.chiave} style={S.modaleColonna}>
                    <button style={S.modalePillTitolo} onClick={() => toggleColonna(col.chiave)}>
                      {col.titolo} {aperta ? '▾' : '▸'}
                    </button>
                    {aperta && voci.map((v) => (
                      <label key={v} style={S.modaleCheckRiga}>
                        <input
                          type="checkbox"
                          style={S.checkboxGrande}
                          checked={(criteri[col.chiave] || []).includes(v)}
                          onChange={() => onCriterio(col.chiave, v)}
                        />
                        {v}
                      </label>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <button style={{ ...S.bottoneProsegui, borderColor: colore, color: colore }} onClick={onProsegui}>PROSEGUI</button>
        </div>
      </div>
    </div>
  );
}

function Alloggio({ strutture, onChange, nottiViaggio, nottiAmici, onNottiAmici, copertura, anagrafica, isMobile, registraIndietro, vaiA, indiceModifica }) {
  const c = CONFIG.colori.alloggio;
  const [percorso, setPercorso] = useState('nome'); // nome | criteri
  const [tipiScelti, setTipiScelti] = useState([]);
  const [codaTipi, setCodaTipi] = useState([]);
  // in modifica il form si apre già compilato con la struttura scelta dalla panoramica
  const [bozza, setBozza] = useState(() => (indiceModifica != null ? { ...strutture[indiceModifica] } : null));
  const [selezionato, setSelezionato] = useState(null);
  const [risultatiVisibili, setRisultatiVisibili] = useState(false);
  const [criteriModalAperto, setCriteriModalAperto] = useState(false);
  const [criteriConfermati, setCriteriConfermati] = useState(false);
  const [criteri, setCriteri] = useState({
    prezzo_da: '', prezzo_a: '', posizione: [], recensioni: [], comfort: [], tipologia: [], servizi: [],
  });
  const [filtriNascosti, setFiltriNascosti] = useState(false);
  const [cercaCriteri, setCercaCriteri] = useState('');

  const toggleTipo = (id) =>
    setTipiScelti((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const nuovaStruttura = (tipo) => {
    // Check-in/out precompilati dalle date del viaggio; una struttura successiva parte dal check-out della precedente.
    const ultima = strutture[strutture.length - 1];
    setBozza({
      nome: '', tipo, date_da: ultima?.date_a || anagrafica.data_partenza, date_a: anagrafica.data_ritorno,
      fascia_prezzo: '', servizi_usati: [],
    });
    setPercorso('nome');
    setCriteriConfermati(false);
    setCriteri({ prezzo_da: '', prezzo_a: '', posizione: [], recensioni: [], comfort: [], tipologia: [], servizi: [] });
  };

  useEffect(() => {
    setRisultatiVisibili(false);
    setSelezionato(null);
  }, [percorso, criteriConfermati]);
  // il nome azzera i risultati solo quando lo si scrive (percorso "nome"): nel percorso "criteri"
  // il nome viene impostato dalla selezione stessa, e azzerare qui farebbe sparire il risultato appena scelto
  useEffect(() => {
    if (percorso !== 'nome') return;
    setRisultatiVisibili(false);
    setSelezionato(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bozza?.nome]);

  const criterio = (chiave, valore, direct) => {
    if (direct) { setCriteri((prev) => ({ ...prev, [chiave]: valore })); return; }
    setCriteri((prev) => {
      const lista = prev[chiave] || [];
      return { ...prev, [chiave]: lista.includes(valore) ? lista.filter((x) => x !== valore) : [...lista, valore] };
    });
  };

  const campiCompleti = percorso === 'nome' ? Boolean(bozza?.nome?.trim()) : criteriConfermati;

  // ⚠️ Dati fittizi: verranno sostituiti dai risultati reali via Google Places (nome o criteri).
  const risultatiFittizi = [
    { id: 1, nome: 'Hotel Piazza Bellini', info: '4 stelle · Centro storico', prezzo: 95 },
    { id: 2, nome: 'B&B Vista Mare', info: '3 stelle · Vicino spiaggia', prezzo: 60 },
    { id: 3, nome: 'Appartamento Toledo', info: 'Intero appartamento · 2 camere', prezzo: 80 },
    { id: 4, nome: 'Ostello dei Viaggiatori', info: 'Camerata condivisa', prezzo: 25 },
    { id: 5, nome: 'Chalet Alpino', info: 'Vista montagna · Camino', prezzo: 120 },
  ];

  const selezionaRisultato = (r) => {
    setSelezionato(r.id);
    setBozza((prev) => ({ ...prev, nome: r.nome }));
  };

  const campiStrutturaCompleti = Boolean(bozza?.date_da && bozza?.date_a);

  const aggiungi = () => {
    if (!campiStrutturaCompleti) return;
    if (indiceModifica != null) {
      onChange(strutture.map((x, j) => (j === indiceModifica ? bozza : x)));
      setBozza(null);
      vaiA('anteprima');
      return;
    }
    onChange([...strutture, bozza]);
    if (codaTipi.length > 0) {
      const [prossimo, ...resto] = codaTipi;
      setCodaTipi(resto);
      nuovaStruttura(prossimo);
    } else {
      setBozza(null);
      vaiA('anteprima');
    }
  };

  const annullaForm = () => {
    setCodaTipi([]);
    setBozza(null);
    setCriteriModalAperto(false);
    if (indiceModifica != null) vaiA('anteprima');
  };

  useEffect(() => {
    if (!registraIndietro) return undefined;
    registraIndietro(bozza ? annullaForm : null);
    return () => registraIndietro(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bozza]);

  const toggleNotteAmici = (n) =>
    onNottiAmici(nottiAmici.includes(n) ? nottiAmici.filter((x) => x !== n) : [...nottiAmici, n]);

  return (
    <section style={{ ...S.sezione, maxWidth: 'min(1300px, 94vw)' }}>
      {/* durante la compilazione il titolo diventa il tipo scelto (es. "Hotel"), con la sua icona di fianco */}
      <IntestazioneSegmento colore={c}
        titolo={(bozza && CONFIG.tipiAlloggio.find((t) => t.id === bozza.tipo)?.label) || 'Alloggio'}
        nota="Dove hai dormito. Se hai dormito da amici, ti basta marcare le notti: nessun dettaglio richiesto."
        iconaMezzo={bozza && <IconaAlloggio tipo={bozza.tipo} colore={c} size={26} />} />

      {!bozza && (
        <>
          <div style={S.alloggioIconeRiga}>
            {CONFIG.tipiAlloggio.map((t) => {
              const attivo = tipiScelti.includes(t.id);
              return (
                <button
                  key={t.id}
                  className="octa-mezzo-icona"
                  style={{ ...S.alloggioIconaMini, opacity: tipiScelti.length === 0 || attivo ? 1 : 0.4 }}
                  onClick={() => toggleTipo(t.id)}
                >
                  <span
                    role="img"
                    aria-label={t.label}
                    style={{
                      ...S.alloggioIconaEmoji,
                      backgroundColor: attivo ? c : '#8c8c8c',
                      WebkitMaskImage: `url(${t.icona})`,
                      maskImage: `url(${t.icona})`,
                    }}
                  />
                  <span style={S.alloggioIconaLabel}>{t.label}</span>
                </button>
              );
            })}
          </div>
          <div style={{ textAlign: 'center' }}>
            <button
              style={{ ...S.bottoneProsegui, borderColor: c, color: c, opacity: tipiScelti.length > 0 ? 1 : 0.4 }}
              disabled={tipiScelti.length === 0}
              onClick={() => {
                if (tipiScelti.length === 0) return;
                const [primo, ...resto] = tipiScelti;
                setCodaTipi(resto);
                nuovaStruttura(primo);
                setTipiScelti([]);
              }}
            >
              PROSEGUI
            </button>
          </div>
        </>
      )}

      {strutture.length > 0 && (
        <ol style={S.listaElementi}>
          {strutture.map((s, i) => (
            <li key={i} style={S.elemento}>
              <IconaAlloggio tipo={s.tipo} colore={c} size={20} />
              <div style={S.elementoTesto}>
                <strong>{s.nome || 'Struttura senza nome'}</strong>
                <span style={S.elementoMeta}>
                  {fmtData(s.date_da)} – {fmtData(s.date_a)} · {nottiTra(s.date_da, s.date_a).length} notti
                </span>
              </div>
              <span style={{ ...S.elementoCosto, fontSize: 12 }}>{s.fascia_prezzo || ''}</span>
              <button style={S.rimuovi} onClick={() => onChange(strutture.filter((_, j) => j !== i))}>×</button>
            </li>
          ))}
        </ol>
      )}

      {bozza && (
        <div style={S.formVolo}>
        <div style={{ ...S.rigaBottoni, ...(isMobile ? {} : { justifyContent: 'flex-start' }) }}>
          <button style={{ ...S.tab, ...(percorso === 'nome' ? { borderColor: c, color: c } : {}) }} onClick={() => setPercorso('nome')}>
            Cerca per nome
          </button>
          <span style={S.oppure}>oppure</span>
          <button
            style={{ ...S.tab, ...(percorso === 'criteri' ? { borderColor: c, color: c } : {}) }}
            onClick={() => { setPercorso('criteri'); setCriteriModalAperto(true); }}
          >
            Seleziona i criteri che hai usato
          </button>
        </div>
        <div style={isMobile ? S.formVoloGridMobile : S.formVoloGrid}>
          <div style={isMobile ? S.rigaVoloGruppoSxMobile : { ...S.rigaVoloGruppoSx, gridColumn: 1, gridRow: 1 }}>
            <div style={{ ...S.campoVolo, alignItems: 'flex-start', minWidth: '100%' }}>
              {percorso === 'nome' ? (
                <>
                  <span style={S.labelVolo}>Nome della struttura</span>
                  <div style={S.inputVoloWrap}>
                    <input className="octa-input-citta" style={{ ...S.inputVolo, textAlign: 'left' }} value={bozza.nome}
                      onChange={(e) => setBozza({ ...bozza, nome: e.target.value })} placeholder="Hotel Piazza Bellini" />
                    <IconaLente />
                  </div>
                  <span style={S.sottoVolo}>Hotel, B&amp;B, appartamento…</span>
                  {/* ⚠️ INNESTO 7 — Google Places autocomplete: precompila tipo, stelle, dotazioni offerte.
                      Poi si chiede all'utente SOLO quali servizi ha effettivamente usato. */}
                </>
              ) : (
                <div style={{ marginTop: 14 }}>
                  {criteriConfermati ? (
                    <>
                      <p style={S.nota}>Criteri selezionati in base alla tua ricerca.</p>
                      <button style={S.linkPiccolo} onClick={() => setCriteriModalAperto(true)}>Modifica i criteri</button>
                    </>
                  ) : (
                    <button style={{ ...S.bottonePrimario, background: c }} onClick={() => setCriteriModalAperto(true)}>
                      Apri i criteri
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {!isMobile && <div style={{ ...S.divisoreVoloGrid, gridColumn: 2, gridRow: '1 / 3' }} />}

          <div style={isMobile ? {} : { minWidth: 300, gridColumn: 3, gridRow: 1 }}>
            {risultatiVisibili && percorso === 'nome' ? (
              <RisultatoAlloggio bozza={bozza} colore={c} selezionato={selezionato === 'nome'}
                onSeleziona={() => setSelezionato(selezionato === 'nome' ? null : 'nome')} />
            ) : risultatiVisibili ? (
              <ListaRisultatiAlloggio risultati={risultatiFittizi} selezionato={selezionato} onSeleziona={selezionaRisultato} colore={c} />
            ) : (
              <button
                style={{ ...S.bottoneProsegui, borderColor: c, color: c, opacity: campiCompleti ? 1 : 0.4, marginTop: 0 }}
                disabled={!campiCompleti}
                onClick={() => setRisultatiVisibili(true)}
              >
                Seleziona risultato
              </button>
            )}
            {/* ⚠️ INNESTO 7 — elenco strutture reali via Google Places, filtrate su nome o criteri */}
          </div>

          <div style={isMobile ? S.rigaVoloGruppoSxMobile : { ...S.rigaVoloGruppoSx, gridColumn: 1, gridRow: 2 }}>
            <div style={{ ...S.campoVolo, alignItems: 'flex-start', ...(isMobile ? { flex: 'none', minWidth: 0, width: '100%' } : {}) }}>
              <span style={S.labelVolo}>Check-in</span>
              <div style={S.inputVoloWrap}>
                <input type="date" style={{ ...S.inputVolo, textAlign: 'left' }} value={bozza.date_da}
                  min={anagrafica.data_partenza} max={anagrafica.data_ritorno}
                  onChange={(e) => setBozza({ ...bozza, date_da: e.target.value })} />
              </div>
            </div>
            <div style={{ ...S.campoVolo, alignItems: 'flex-start', ...(isMobile ? { flex: 'none', minWidth: 0, width: '100%' } : {}) }}>
              <span style={S.labelVolo}>Check-out</span>
              <div style={S.inputVoloWrap}>
                <input type="date" style={{ ...S.inputVolo, textAlign: 'left' }} value={bozza.date_a}
                  min={anagrafica.data_partenza} max={anagrafica.data_ritorno}
                  onChange={(e) => setBozza({ ...bozza, date_a: e.target.value })} />
              </div>
            </div>
            <div style={{ ...S.campoVolo, alignItems: 'flex-start', ...(isMobile ? { flex: 'none', minWidth: 0, width: '100%' } : {}) }}>
              <span style={S.labelVolo}>Fascia di prezzo per notte (facoltativo)</span>
              <select
                style={{ ...S.pillTariffa, borderColor: c, color: c, marginTop: 8 }}
                value={bozza.fascia_prezzo}
                onChange={(e) => setBozza({ ...bozza, fascia_prezzo: e.target.value })}
              >
                <option value="">LA TUA TARIFFA ▾</option>
                {CONFIG.fasceTariffa.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div style={S.rigaBottoni}>
          <button style={S.bottoneSecondario} onClick={annullaForm}>
            Annulla
          </button>
          <button
            style={{ ...S.bottonePrimario, background: c, padding: '6px 22px', opacity: campiStrutturaCompleti ? 1 : 0.4 }}
            disabled={!campiStrutturaCompleti}
            onClick={aggiungi}
          >
            {indiceModifica != null ? 'Salva modifiche' : 'Aggiungi all’itinerario'}
          </button>
        </div>
        </div>
      )}

      {criteriModalAperto && (
        <ModaleCriteriAlloggio
          colore={c}
          criteri={criteri}
          onCriterio={criterio}
          filtriNascosti={filtriNascosti}
          onNascondi={setFiltriNascosti}
          cerca={cercaCriteri}
          onCerca={setCercaCriteri}
          onChiudi={() => setCriteriModalAperto(false)}
          onProsegui={() => { setCriteriConfermati(true); setCriteriModalAperto(false); }}
        />
      )}

      {nottiViaggio.length > 0 && (
        <div style={{ ...S.form, border: 'none', background: 'transparent', padding: '18px 0' }}>
          <p style={S.etichettaGruppo}>Notti da amici o familiari</p>
          <p style={S.nota}>Tocca le notti in cui hai dormito da qualcuno. Sono a costo zero e non chiedono altro.</p>
          <div style={S.grigliaNotti}>
            {nottiViaggio.map((n) => {
              const inStruttura = copertura.coperte.has(n) && !nottiAmici.includes(n);
              const scelta = nottiAmici.includes(n);
              return (
                <button key={n} disabled={inStruttura}
                  onClick={() => toggleNotteAmici(n)}
                  style={{
                    ...S.notteBottone,
                    background: scelta ? c : inStruttura ? CONFIG.colori.chiaro : '#fff',
                    color: scelta ? '#fff' : inStruttura ? CONFIG.colori.testoSoft : CONFIG.colori.dark,
                    cursor: inStruttura ? 'default' : 'pointer',
                  }}>
                  {fmtData(n)}
                </button>
              );
            })}
          </div>
          {copertura.scoperte.length > 0 && (
            <p style={S.avviso}>Restano {copertura.scoperte.length} notti senza copertura.</p>
          )}
        </div>
      )}
    </section>
  );
}

// Icone dei tipi di attività e servizio (stesso stile dei mezzi di Trasporto: path 24×24 a tinta unica).
const TIPO_PATH = {
  museo: 'M4 10v7h3v-7H4zm6 0v7h3v-7h-3zM2 22h19v-3H2v3zm14-12v7h3v-7h-3zm-4.5-9L2 6v2h19V6l-9.5-5z',
  escursione: 'M14 6l-3.75 5 2.85 3.8-1.6 1.2C9.81 13.75 7 10 7 10l-6 8h22L14 6z',
  tour: 'M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z',
  corso: 'M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z',
  gastronomia: 'M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z',
  serata: 'M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z',
  sport: 'M15.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM5 12c-2.8 0-5 2.2-5 5s2.2 5 5 5 5-2.2 5-5-2.2-5-5-5zm0 8.5c-1.9 0-3.5-1.6-3.5-3.5s1.6-3.5 3.5-3.5 3.5 1.6 3.5 3.5-1.6 3.5-3.5 3.5zm5.8-10l2.4-2.4.8.8c1.3 1.3 3 2.1 5.1 2.1V9c-1.5 0-2.7-.6-3.6-1.5l-1.9-1.9c-.5-.4-1-.6-1.6-.6s-1.1.2-1.4.6L7.8 8.4c-.4.4-.6.9-.6 1.4 0 .6.2 1.1.6 1.4L11 14v5h2v-6.2l-2.2-2.3zM19 12c-2.8 0-5 2.2-5 5s2.2 5 5 5 5-2.2 5-5-2.2-5-5-5zm0 8.5c-1.9 0-3.5-1.6-3.5-3.5s1.6-3.5 3.5-3.5 3.5 1.6 3.5 3.5-1.6 3.5-3.5 3.5z',
  bambini: 'M13 2v8h8c0-4.42-3.58-8-8-8zm6.32 13.89C20.37 14.54 21 12.84 21 11H6.44l-.95-2H2v2h2.22s1.89 4.07 2.12 4.42c-1.1.59-1.84 1.75-1.84 3.08C4.5 20.43 6.07 22 8 22c1.76 0 3.22-1.3 3.46-3h2.08c.24 1.7 1.7 3 3.46 3 1.93 0 3.5-1.57 3.5-3.5 0-1.04-.46-1.97-1.18-2.61zM8 20c-.83 0-1.5-.67-1.5-1.5S7.17 17 8 17s1.5.67 1.5 1.5S8.83 20 8 20zm9 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z',
  cinema: 'M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z',
  locali: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
  noleggio_auto: 'M5 11l1.4-4.2A2 2 0 0 1 8.3 5.4h7.4a2 2 0 0 1 1.9 1.4L19 11h.3A1.7 1.7 0 0 1 21 12.7v3.6a1.7 1.7 0 0 1-1.7 1.7H19v1a1 1 0 0 1-1 1h-.8a1 1 0 0 1-1-1v-1H7.8v1a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-1h-.3A1.7 1.7 0 0 1 3 16.3v-3.6A1.7 1.7 0 0 1 4.7 11H5zm1.8 0h10.4l-1.1-3.3a.5.5 0 0 0-.5-.3H8.4a.5.5 0 0 0-.5.3L6.8 11zM6.4 14a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2zm11.2 0a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2z',
  noleggio_camper: 'M17 5H3c-1.1 0-2 .89-2 2v9h2c0 1.65 1.34 3 3 3s3-1.35 3-3h5.5c0 1.65 1.34 3 3 3s3-1.35 3-3H23v-5l-6-6zM3 11V7h4v4H3zm3 6.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM13 11H9V7h4v4zm4.5 6.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM15 11V7h1l4 4h-5z',
  noleggio_barca: 'M20 21c-1.39 0-2.78-.47-4-1.32-2.44 1.71-5.56 1.71-8 0C6.78 20.53 5.39 21 4 21H2v2h2c1.38 0 2.74-.35 4-.99 2.52 1.29 5.48 1.29 8 0 1.26.65 2.62.99 4 .99h2v-2h-2zM3.95 19H4c1.6 0 3.02-.88 4-2 .98 1.12 2.4 2 4 2s3.02-.88 4-2c.98 1.12 2.4 2 4 2h.05l1.89-6.68c.08-.26.06-.54-.06-.78s-.34-.42-.6-.5L20 10.62V6c0-1.1-.9-2-2-2h-3V1H9v3H6c-1.1 0-2 .9-2 2v4.62l-1.29.42c-.26.08-.48.26-.6.5s-.15.52-.06.78L3.95 19zM6 6h12v3.97L12 8 6 9.97V6z',
  noleggio_moto: 'M19.44 9.03L15.41 5H11v2h3.59l2 2H5c-2.8 0-5 2.2-5 5s2.2 5 5 5c2.46 0 4.45-1.69 4.9-4h1.65l2.77-2.77c-.21.54-.32 1.14-.32 1.77 0 2.8 2.2 5 5 5s5-2.2 5-5c0-2.65-1.97-4.77-4.56-4.97zM7.82 15C7.4 16.15 6.28 17 5 17c-1.63 0-3-1.37-3-3s1.37-3 3-3c1.28 0 2.4.85 2.82 2H5v2h2.82zM19 17c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z',
  assicurazione: 'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z',
  parcheggio: 'M13 3H6v18h4v-6h3c3.31 0 6-2.69 6-6s-2.69-6-6-6zm.2 8H10V7h3.2c1.1 0 2 .9 2 2s-.9 2-2 2z',
  altro: 'M6 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm12 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm-6 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z',
};

function IconaTipo({ tipo, colore, size = 26 }) {
  if (!TIPO_PATH[tipo]) return null;
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill={colore} style={{ flexShrink: 0 }}>
      <path d={TIPO_PATH[tipo]} />
    </svg>
  );
}

// Riga di icone a scelta multipla + PROSEGUI: stesso schema della scelta mezzo (Trasporto) e tipo struttura (Alloggio).
function SceltaTipi({ tipi, scelti, onScegli, onProsegui, colore }) {
  return (
    <>
      <div style={S.alloggioIconeRiga}>
        {tipi.map((t) => {
          const attivo = scelti.includes(t.id);
          return (
            <button
              key={t.id}
              className="octa-tipo-icona"
              style={{ ...S.alloggioIconaMini, '--hover': colore, opacity: scelti.length === 0 || attivo ? 1 : 0.4 }}
              onClick={() => onScegli(t.id)}
            >
              <svg viewBox="0 0 24 24" width="44" height="44" className="octa-tipo-svg" fill={attivo ? colore : '#8c8c8c'}>
                <path d={TIPO_PATH[t.id]} />
              </svg>
              <span style={S.alloggioIconaLabel}>{t.label}</span>
            </button>
          );
        })}
      </div>
      <div style={{ textAlign: 'center' }}>
        <button
          style={{ ...S.bottoneProsegui, borderColor: colore, color: colore, opacity: scelti.length > 0 ? 1 : 0.4 }}
          disabled={scelti.length === 0}
          onClick={onProsegui}
        >
          PROSEGUI
        </button>
      </div>
    </>
  );
}

// Coda dei tipi scelti: si compila un form per tipo; finita la coda si torna alla panoramica dei 4 segmenti.
function useCodaTipi({ lista, onChange, vaiA, registraIndietro, nuovaBozza, indiceModifica }) {
  const [scelti, setScelti] = useState([]);
  const [coda, setCoda] = useState([]);
  // in modifica il form si apre già compilato con l'elemento scelto dalla panoramica
  const [bozza, setBozza] = useState(() => (indiceModifica != null ? { ...lista[indiceModifica] } : null));

  const scegli = (id) => setScelti((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const prosegui = () => {
    if (scelti.length === 0) return;
    const [primo, ...resto] = scelti;
    setCoda(resto);
    setBozza(nuovaBozza(primo));
    setScelti([]);
  };
  const aggiungi = () => {
    if (indiceModifica != null) {
      onChange(lista.map((x, j) => (j === indiceModifica ? bozza : x)));
      setBozza(null);
      vaiA('anteprima');
      return;
    }
    onChange([...lista, bozza]);
    if (coda.length > 0) {
      const [prossimo, ...resto] = coda;
      setCoda(resto);
      setBozza(nuovaBozza(prossimo));
    } else {
      setBozza(null);
      vaiA('anteprima');
    }
  };
  const annulla = () => {
    setCoda([]);
    setBozza(null);
    if (indiceModifica != null) vaiA('anteprima');
  };

  useEffect(() => {
    if (!registraIndietro) return undefined;
    registraIndietro(bozza ? annulla : null);
    return () => registraIndietro(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bozza]);

  return { scelti, scegli, prosegui, bozza, setBozza, aggiungi, annulla };
}

/* ============================================================
   SEGMENTO ATTIVITÀ
   ============================================================ */
function Attivita({ lista, onChange, anagrafica, vaiA, isMobile, registraIndietro, indiceModifica }) {
  const c = CONFIG.colori.attivita;
  const [selezionato, setSelezionato] = useState(null);
  const [risultatiVisibili, setRisultatiVisibili] = useState(false);
  const { scelti, scegli, prosegui, bozza, setBozza, aggiungi, annulla } = useCodaTipi({
    lista, onChange, vaiA, registraIndietro, indiceModifica,
    nuovaBozza: (tipo) => ({ tipo, nome: '', luogo: anagrafica.destinazione, data: '', prezzo: '', note: '' }),
  });

  useEffect(() => {
    setRisultatiVisibili(false);
    setSelezionato(null);
  }, [bozza?.nome, bozza?.tipo]);

  // ⚠️ Dati fittizi: verranno sostituiti dai risultati reali via Google Places / fornitori di esperienze.
  const risultatiFittizi = [
    { id: 1, nome: 'Visita guidata del centro storico', info: '2 ore · In italiano', prezzo: 25 },
    { id: 2, nome: 'Ingresso museo + audioguida', info: 'Salta la fila', prezzo: 18 },
    { id: 3, nome: 'Escursione di mezza giornata', info: 'Trasferimento incluso', prezzo: 45 },
    { id: 4, nome: 'Corso di cucina tipica', info: '3 ore · Pranzo incluso', prezzo: 70 },
    { id: 5, nome: 'Tour serale in barca', info: 'Aperitivo a bordo', prezzo: 35 },
  ];

  const selezionaRisultato = (r) => {
    setSelezionato(r.id);
    setBozza((prev) => ({ ...prev, nome: r.nome, prezzo: prev.prezzo || String(r.prezzo) }));
  };

  const campo = { ...S.campoVolo, alignItems: 'flex-start', ...(isMobile ? { flex: 'none', minWidth: 0, width: '100%' } : {}) };
  const completo = Boolean(bozza?.nome?.trim());

  return (
    <section style={{ ...S.sezione, maxWidth: 'min(1300px, 94vw)' }}>
      <IntestazioneSegmento colore={c} titolo="Attività"
        nota="Cosa hai fatto: visite, escursioni, musei, corsi, serate."
        iconaMezzo={bozza && <IconaTipo tipo={bozza.tipo} colore={c} />} />

      {!bozza && <SceltaTipi tipi={CONFIG.tipiAttivita} scelti={scelti} onScegli={scegli} onProsegui={prosegui} colore={c} />}

      {lista.length > 0 && (
        <ol style={S.listaElementi}>
          {lista.map((a, i) => (
            <li key={i} style={S.elemento}>
              <IconaTipo tipo={a.tipo} colore={c} size={20} />
              <div style={S.elementoTesto}>
                <strong>{a.nome}</strong>
                <span style={S.elementoMeta}>{a.luogo} {a.data ? `· ${fmtData(a.data)}` : ''}</span>
              </div>
              <span style={S.elementoCosto}>{euro(a.prezzo)}</span>
              <button style={S.rimuovi} onClick={() => onChange(lista.filter((_, j) => j !== i))}>×</button>
            </li>
          ))}
        </ol>
      )}

      {bozza && (
        <div style={S.formVolo}>
          <div style={isMobile ? S.formVoloGridMobile : S.formVoloGrid}>
            <div style={isMobile ? S.rigaVoloGruppoSxMobile : { ...S.rigaVoloGruppoSx, gridColumn: 1, gridRow: 1 }}>
              <div style={{ ...S.campoVolo, alignItems: 'flex-start', minWidth: '100%' }}>
                <span style={S.labelVolo}>Cosa hai fatto</span>
                <div style={S.inputVoloWrap}>
                  <input className="octa-input-citta" style={{ ...S.inputVolo, textAlign: 'left' }} value={bozza.nome}
                    onChange={(e) => setBozza({ ...bozza, nome: e.target.value })} placeholder="Scavi di Pompei" />
                  <IconaLente />
                </div>
                <span style={S.sottoVolo}>Museo, tour, escursione, corso…</span>
              </div>
            </div>

            {!isMobile && <div style={{ ...S.divisoreVoloGrid, gridColumn: 2, gridRow: '1 / 3' }} />}

            <div style={isMobile ? {} : { minWidth: 300, gridColumn: 3, gridRow: 1 }}>
              {risultatiVisibili ? (
                <ListaRisultatiAlloggio risultati={risultatiFittizi} selezionato={selezionato} onSeleziona={selezionaRisultato}
                  colore={c} intestazione="Attività" colonnaPrezzo="Prezzo" />
              ) : (
                <button
                  style={{ ...S.bottoneProsegui, borderColor: c, color: c, opacity: completo ? 1 : 0.4, marginTop: 0 }}
                  disabled={!completo}
                  onClick={() => setRisultatiVisibili(true)}
                >
                  Seleziona risultato
                </button>
              )}
              {/* ⚠️ INNESTO — elenco attività reali (Google Places / fornitori), filtrate sul nome */}
            </div>

            <div style={isMobile ? S.rigaVoloGruppoSxMobile : { ...S.rigaVoloGruppoSx, gridColumn: 1, gridRow: 2 }}>
              <div style={campo}>
                <span style={S.labelVolo}>Dove</span>
                <div style={S.inputVoloWrap}>
                  <input className="octa-input-citta" style={{ ...S.inputVolo, textAlign: 'left' }} value={bozza.luogo}
                    onChange={(e) => setBozza({ ...bozza, luogo: e.target.value })} placeholder="Pompei" />
                </div>
              </div>
              <div style={campo}>
                <span style={S.labelVolo}>Quando (facoltativo)</span>
                <div style={S.inputVoloWrap}>
                  <input type="date" style={{ ...S.inputVolo, textAlign: 'left' }} value={bozza.data}
                    min={anagrafica.data_partenza} max={anagrafica.data_ritorno}
                    onChange={(e) => setBozza({ ...bozza, data: e.target.value })} />
                </div>
              </div>
              <div style={campo}>
                <span style={S.labelVolo}>Quanto è costata (facoltativo)</span>
                <div style={S.inputVoloWrap}>
                  <input type="number" style={{ ...S.inputVolo, textAlign: 'left' }} value={bozza.prezzo}
                    onChange={(e) => setBozza({ ...bozza, prezzo: e.target.value })} placeholder="€" />
                </div>
              </div>
            </div>
          </div>

          <div style={S.rigaBottoni}>
            <button style={S.bottoneSecondario} onClick={annulla}>Annulla</button>
            <button
              style={{ ...S.bottonePrimario, background: c, padding: '6px 22px', opacity: completo ? 1 : 0.4 }}
              disabled={!completo}
              onClick={aggiungi}
            >
              {indiceModifica != null ? 'Salva modifiche' : 'Aggiungi all’itinerario'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/* ============================================================
   SEGMENTO SERVIZI
   Qui vive il canone di noleggio (l'AVERE il mezzo) e il flag copre_alloggio.
   ============================================================ */
function Servizi({ lista, onChange, anagrafica, veicoloDalTrasporto, vaiA, isMobile, registraIndietro, indiceModifica }) {
  const c = CONFIG.colori.servizi;
  const { scelti, scegli, prosegui, bozza, setBozza, aggiungi, annulla } = useCodaTipi({
    lista, onChange, vaiA, registraIndietro, indiceModifica,
    // Date precompilate con quelle del viaggio: il caso più comune è un servizio che dura tutto il soggiorno.
    nuovaBozza: (tipo) => ({
      tipo, descrizione: '', data_inizio: anagrafica.data_partenza, data_fine: anagrafica.data_ritorno, prezzo: '',
      copre_alloggio: CONFIG.tipiServizio.find((t) => t.id === tipo)?.copreAlloggio || false, veicolo: '',
    }),
  });

  const ereditaVeicolo = () =>
    setBozza({
      tipo: 'noleggio_auto', descrizione: '', prezzo: '', copre_alloggio: false,
      ...bozza,
      veicolo: veicoloDalTrasporto.veicolo,
      data_inizio: anagrafica.data_partenza,
      data_fine: anagrafica.data_ritorno,
    });

  const giorni = bozza ? nottiTra(bozza.data_inizio, bozza.data_fine).length : 0;
  const alGiorno = giorni > 0 && bozza?.prezzo ? Number(bozza.prezzo) / giorni : null;
  const campo = { ...S.campoVolo, alignItems: 'flex-start', ...(isMobile ? { flex: 'none', minWidth: 0, width: '100%' } : {}) };

  return (
    <section style={{ ...S.sezione, maxWidth: 'min(1300px, 94vw)' }}>
      <IntestazioneSegmento colore={c} titolo="Servizi"
        nota="Il costo di avere il mezzo, non di muoverlo: qui va il canone di noleggio, insieme ad assicurazioni e parcheggi."
        iconaMezzo={bozza && <IconaTipo tipo={bozza.tipo} colore={c} />} />

      {veicoloDalTrasporto && !lista.some((s) => s.veicolo) && !bozza?.veicolo && (
        <div style={{ ...S.suggerimento, borderColor: c }}>
          Nel trasporto hai usato un veicolo. Hai continuato a usarlo anche per il resto dei giorni?
          <button style={{ ...S.bottoneMini, background: c }} onClick={ereditaVeicolo}>
            Sì, riprendi quei dati
          </button>
        </div>
      )}

      {!bozza && <SceltaTipi tipi={CONFIG.tipiServizio} scelti={scelti} onScegli={scegli} onProsegui={prosegui} colore={c} />}

      {lista.length > 0 && (
        <ol style={S.listaElementi}>
          {lista.map((s, i) => (
            <li key={i} style={S.elemento}>
              <IconaTipo tipo={s.tipo} colore={c} size={20} />
              <div style={S.elementoTesto}>
                <strong>{CONFIG.tipiServizio.find((t) => t.id === s.tipo)?.label || 'Servizio'}</strong>
                <span style={S.elementoMeta}>
                  {s.data_inizio ? `${fmtData(s.data_inizio)} – ${fmtData(s.data_fine)}` : 'senza date'}
                  {s.copre_alloggio ? ' · copre l’alloggio' : ''}
                </span>
              </div>
              <span style={S.elementoCosto}>{euro(s.prezzo)}</span>
              <button style={S.rimuovi} onClick={() => onChange(lista.filter((_, j) => j !== i))}>×</button>
            </li>
          ))}
        </ol>
      )}

      {bozza && (
        <div style={S.formVolo}>
          <div style={{ ...(isMobile ? S.rigaVoloGruppoSxMobile : S.rigaVoloGruppoSx), textAlign: 'left' }}>
            <div style={campo}>
              <span style={S.labelVolo}>Fornitore o dettagli (facoltativo)</span>
              <div style={S.inputVoloWrap}>
                <input className="octa-input-citta" style={{ ...S.inputVolo, textAlign: 'left' }} value={bozza.descrizione}
                  onChange={(e) => setBozza({ ...bozza, descrizione: e.target.value })} placeholder="Hertz, Fiat 500…" />
              </div>
            </div>
            <div style={campo}>
              <span style={S.labelVolo}>Canone totale</span>
              <div style={S.inputVoloWrap}>
                <input type="number" style={{ ...S.inputVolo, textAlign: 'left' }} value={bozza.prezzo}
                  onChange={(e) => setBozza({ ...bozza, prezzo: e.target.value })} placeholder="€" />
              </div>
              {alGiorno !== null && <span style={S.sottoVolo}>Sono {euro(alGiorno)} al giorno su {giorni} giorni.</span>}
            </div>
            <div style={campo}>
              <span style={S.labelVolo}>Dal</span>
              <div style={S.inputVoloWrap}>
                <input type="date" style={{ ...S.inputVolo, textAlign: 'left' }} value={bozza.data_inizio}
                  min={anagrafica.data_partenza} max={anagrafica.data_ritorno}
                  onChange={(e) => setBozza({ ...bozza, data_inizio: e.target.value })} />
              </div>
            </div>
            <div style={campo}>
              <span style={S.labelVolo}>Al</span>
              <div style={S.inputVoloWrap}>
                <input type="date" style={{ ...S.inputVolo, textAlign: 'left' }} value={bozza.data_fine}
                  min={anagrafica.data_partenza} max={anagrafica.data_ritorno}
                  onChange={(e) => setBozza({ ...bozza, data_fine: e.target.value })} />
              </div>
            </div>
          </div>

          <label style={{ ...S.checkRiga, justifyContent: 'flex-start', marginTop: 20 }}>
            <input type="checkbox" checked={bozza.copre_alloggio}
              onChange={(e) => setBozza({ ...bozza, copre_alloggio: e.target.checked })} />
            Ci hai anche dormito (camper, van, barca): copre l’alloggio per queste notti
          </label>

          <div style={S.rigaBottoni}>
            <button style={S.bottoneSecondario} onClick={annulla}>Annulla</button>
            <button style={{ ...S.bottonePrimario, background: c, padding: '6px 22px' }} onClick={aggiungi}>
              {indiceModifica != null ? 'Salva modifiche' : 'Aggiungi all’itinerario'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/* ============================================================
   FASCIA DI PREZZO — obbligatoria
   ============================================================ */
function FasciaPrezzo({ valore, onChange, titolo, onTitolo, costoTotale }) {
  return (
    <section style={S.sezione}>
      <IntestazioneSegmento colore={CONFIG.colori.dark} titolo="Fascia di prezzo"
        nota="Senza fascia di prezzo l’esperienza non si salva: è ciò che la rende utile a chi la legge." />

      <Campo label="Titolo dell’esperienza (facoltativo)">
        <input style={S.input} value={titolo} onChange={(e) => onTitolo(e.target.value)} placeholder="Quattro giorni a Napoli in auto" />
      </Campo>

      <div style={S.griglia2}>
        <Campo label="Da">
          <input type="number" style={S.input} value={valore.min}
            onChange={(e) => onChange({ ...valore, min: e.target.value })} placeholder="€ 400" />
        </Campo>
        <Campo label="A">
          <input type="number" style={S.input} value={valore.max}
            onChange={(e) => onChange({ ...valore, max: e.target.value })} placeholder="€ 600" />
        </Campo>
      </div>

      {costoTotale > 0 && (
        <p style={S.nota}>Dai costi che hai inserito risulta {euro(costoTotale)}. Puoi usarlo come riferimento.</p>
      )}
      <p style={S.nota}>Il prezzo può variare in base a periodo, disponibilità e altri fattori.</p>
    </section>
  );
}

/* ============================================================
   CHECK DI COMPLETAMENTO — 3 uscite
   ============================================================ */
function CheckCompletamento({ errori, aiuto, aiutoCaricamento, onAiuto, onContinua, onFinito, salvataggio, onChiudi }) {
  return (
    <div style={S.overlay} onClick={onChiudi}>
      <div style={S.modale} onClick={(e) => e.stopPropagation()}>
        <h2 style={S.h2}>
          {errori.length > 0
            ? 'Sembrerebbe che manchino alcuni pezzi al tuo puzzle'
            : 'L’esperienza è pronta'}
        </h2>

        {errori.length > 0 && (
          <ul style={S.listaErrori}>
            {errori.map((e, i) => <li key={i} style={S.errore}>{e}</li>)}
          </ul>
        )}

        {aiuto && (
          <div style={S.riquadroAiuto}>
            {aiuto.map((d, i) => <p key={i} style={S.rigaAiuto}>{d}</p>)}
          </div>
        )}

        {salvataggio.stato === 'ok' && <p style={S.successo}>{salvataggio.messaggio}</p>}
        {salvataggio.stato === 'errore' && <p style={S.errore}>{salvataggio.messaggio}</p>}

        <div style={S.rigaBottoni}>
          <button style={S.bottoneSecondario} onClick={onContinua}>Manca qualcosa, continuo</button>
          <button style={S.bottoneSecondario} onClick={onAiuto} disabled={aiutoCaricamento}>
            {aiutoCaricamento ? 'Sto pensando…' : 'Non lo so, aiutami'}
          </button>
          <button style={S.bottonePrimario} onClick={onFinito} disabled={salvataggio.stato === 'invio'}>
            {salvataggio.stato === 'invio' ? 'Salvo…' : 'Sono sicuro, ho finito'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   PICCOLI COMPONENTI
   ============================================================ */
function Campo({ label, children, center, labelStyle }) {
  return (
    <label style={center ? { ...S.campo, alignItems: 'center' } : S.campo}>
      <span style={labelStyle || S.labelCampo}>{label}</span>
      {children}
    </label>
  );
}

function IntestazioneSegmento({ colore, titolo, nota, iconaMezzo }) {
  return (
    <div style={S.intestazioneSegmento}>
      <div style={S.intestazioneSegmentoRiga}>
        <h1 style={iconaMezzo ? { ...S.h1, color: colore, margin: 0 } : { ...S.h1, color: colore }}>{titolo}</h1>
        {iconaMezzo}
      </div>
      <p style={iconaMezzo ? { ...S.sottotitolo, marginTop: 6 } : S.sottotitolo}>{nota}</p>
    </div>
  );
}

/* ============================================================
   STILI (inline, come da convenzione di progetto)
   ============================================================ */
const S = {
  pagina: {
    fontFamily: "Roboto, 'Segoe UI', sans-serif",
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: `${CONFIG.colori.chiaro} url('/images/sfondo.jpg') center bottom / cover no-repeat`,
    backgroundBlendMode: 'lighten',
    color: CONFIG.colori.dark,
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 28px', background: CONFIG.colori.dark, color: '#fff',
    position: 'relative',
  },
  logo: { fontWeight: 900, fontSize: 22, letterSpacing: 2 },
  logoHeaderCentrato: {
    height: 32, objectFit: 'contain',
    position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
  },
  navHeader: { display: 'flex', gap: 18 },
  linkHeader: {
    background: 'none', border: 'none', color: '#fff', fontSize: 13,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  linkIndietro: {
    background: 'none', border: 'none', color: '#fff', fontSize: 24,
    cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1, padding: 4,
  },
  corpo: { display: 'flex', flex: 1, alignItems: 'stretch' },

  riepilogoChiuso: {
    flex: 1, color: '#fff', cursor: 'pointer',
    display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '14px 0', gap: 12,
  },
  riepilogoVerticale: {
    writingMode: 'vertical-rl', fontWeight: 700, fontSize: 13, letterSpacing: 1,
  },
  frecciaRiepilogo: { fontSize: 18 },
  iconeSegmenti: { display: 'flex', flexDirection: 'column', gap: 4, marginTop: 'auto' },
  pallinoSegmento: { fontSize: 12 },
  riepilogoChiusoMobile: {
    width: '100%', color: '#fff', cursor: 'pointer',
    display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px', boxSizing: 'border-box',
  },
  riepilogoOrizzontale: { fontWeight: 700, fontSize: 13, letterSpacing: 1 },
  iconeSegmentiMobile: { display: 'flex', flexDirection: 'row', gap: 6 },

  riepilogoBox: {
    background: '#fff', border: '2px solid', borderTop: 'none',
    overflow: 'hidden', flexShrink: 0, flexDirection: 'column',
    display: 'flex', transition: 'width 0.25s ease', minHeight: 'calc(100vh - 160px)',
  },
  riepilogoHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 14px', color: '#fff', fontWeight: 700, letterSpacing: 1,
  },
  chiudiRiepilogo: { background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer' },
  riepilogoCorpo: { padding: 14 },
  cardRiepilogo: {
    border: `1px solid ${CONFIG.colori.bordo}`, borderRadius: 6, padding: '8px 10px', marginBottom: 8,
  },
  cardTitolo: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600 },
  cardSottotitolo: { fontSize: 12, color: CONFIG.colori.testoSoft, marginTop: 2 },
  cardCosto: { fontSize: 13, fontWeight: 700, marginTop: 4 },
  vuoto: { fontSize: 13, color: CONFIG.colori.testoSoft },
  totale: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    borderTop: `1px solid ${CONFIG.colori.bordo}`, marginTop: 10, paddingTop: 10,
    fontSize: 12, letterSpacing: 0.5, marginBottom: 12,
  },
  bottoneAggiungi: {
    display: 'block', width: '100%', border: 'none', borderRadius: 6, color: '#fff',
    padding: '9px 0', marginBottom: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  },
  bottoneInfBase: {
    display: 'block', width: '50%', margin: '4px auto 0', border: '1px solid #000', borderRadius: 6,
    background: '#fff', color: '#000',
    padding: '5px 0', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  },

  centro: { flex: 1, position: 'relative', padding: 24 },
  boxViaggio: {
    position: 'absolute', top: 0, right: 0, maxWidth: '60%', background: '#fff',
    border: `1px solid ${CONFIG.colori.bordo}`, borderRadius: 8, padding: '8px 14px', fontSize: 12,
  },
  boxViaggioRigaOrizzontale: {
    display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap',
    color: CONFIG.colori.testoSoft,
  },
  boxViaggioSep: { color: CONFIG.colori.bordo },
  boxViaggioMobile: { marginBottom: 16 },
  boxViaggioToggle: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
    background: '#fff', border: `1px solid ${CONFIG.colori.bordo}`, borderRadius: 8,
    padding: '10px 14px', fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
    color: CONFIG.colori.dark,
  },
  boxViaggioMobileContenuto: {
    border: `1px solid ${CONFIG.colori.bordo}`, borderTop: 'none', borderRadius: '0 0 8px 8px',
    padding: '10px 14px', fontSize: 12, overflowX: 'auto',
  },

  sezione: {
    background: 'transparent', padding: 28, maxWidth: 720, border: 'none', margin: '40px auto 0',
  },
  intestazioneSegmento: { marginBottom: 18 },
  intestazioneSegmentoRiga: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  trasportoIconeRiga: { display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 36, flexWrap: 'wrap', margin: '10px 0 26px' },
  trasportoIconaMini: {
    background: 'transparent', border: 'none', borderRadius: 12, padding: 10,
    cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
  },
  trasportoIconaLabel: { fontSize: 14, color: CONFIG.colori.testoSoft, fontFamily: 'inherit' },
  h1: { fontSize: 26, margin: '0 0 6px', fontWeight: 700 },
  h2: { fontSize: 20, margin: '0 0 12px', fontWeight: 700 },
  sottotitolo: { color: CONFIG.colori.testoSoft, fontSize: 14, margin: '0 0 18px' },
  nota: { fontSize: 13, color: CONFIG.colori.testoSoft, margin: '8px 0' },
  avviso: { fontSize: 13, color: CONFIG.colori.trasporto, margin: '8px 0' },
  successo: { fontSize: 14, color: CONFIG.colori.servizi },
  etichettaGruppo: { fontSize: 13, fontWeight: 600, margin: '14px 0 8px' },

  griglia2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 },
  campo: { display: 'flex', flexDirection: 'column', gap: 5 },
  labelCampo: { fontSize: 17, fontWeight: 600, color: CONFIG.colori.testoSoft },
  input: {
    padding: '9px 2px', border: 'none', borderBottom: `1px solid ${CONFIG.colori.bordo}`,
    borderRadius: 0, background: 'transparent', fontSize: 18, fontFamily: 'inherit',
    width: '100%', boxSizing: 'border-box',
  },
  checkRiga: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, margin: '12px 0' },

  sceltaMezzo: { marginTop: 32, textAlign: 'center' },
  sceltaMezzoIcona: {
    background: 'transparent', border: 'none', borderRadius: 12, padding: 10,
    cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'flex-end', gap: 8,
  },
  sceltaMezzoIconaBox: { width: 110, height: 110, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  sceltaMezzoRigaSecondaria: { display: 'flex', justifyContent: 'center', gap: 20, flexWrap: 'wrap', margin: '12px 0' },
  iconaSecondariaGrande: { fontSize: 56, lineHeight: 1 },
  iconaPlaceholderLabel: { fontSize: 14, color: '#7f7f7f', fontFamily: 'inherit' },

  bottoneCerca: {
    background: CONFIG.colori.trasporto, color: '#fff', border: 'none', borderRadius: 6,
    padding: '14px 32px', fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
    alignSelf: 'center', marginTop: 26,
  },
  duColonneAuto: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 48, marginTop: 40, justifyContent: 'center' },
  rigaToggle: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, fontSize: 16, margin: '20px 0', cursor: 'pointer' },
  toggleTrack: {
    display: 'inline-flex', alignItems: 'center', width: 44, height: 24, borderRadius: 14,
    background: '#333', padding: 2, boxSizing: 'border-box', transition: 'background 0.15s',
  },
  toggleTrackOn: { background: CONFIG.colori.trasporto },
  toggleThumb: { width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'transform 0.15s', display: 'block' },
  toggleThumbOn: { transform: 'translateX(20px)' },
  rigaBandiere: { display: 'flex', justifyContent: 'center', gap: 14, marginTop: 8 },
  bandiera: { background: 'none', border: 'none', fontSize: 30, cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1, padding: 0 },
  pillMezziAuto: { display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 22 },
  pillMezzoAuto: {
    border: `1px solid ${CONFIG.colori.bordo}`, borderRadius: 22, background: '#fff', color: CONFIG.colori.dark,
    padding: '10px 22px', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  },
  pillMezzoAutoAttivo: { background: CONFIG.colori.dark, color: '#fff', borderColor: CONFIG.colori.dark },
  rigaTarga: { display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap', margin: '14px 0 8px' },
  boxTarga: {
    width: 32, height: 40, textAlign: 'center', border: `1px solid ${CONFIG.colori.bordo}`, borderRadius: 5,
    fontSize: 17, fontFamily: 'inherit', textTransform: 'uppercase', padding: 0,
  },
  targaCaption: { fontSize: 14, color: CONFIG.colori.testoSoft, margin: '22px 0 6px' },
  grigliaVeicoli: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 14, marginTop: 14, justifyContent: 'center' },
  cardVeicolo: {
    border: `1px solid ${CONFIG.colori.bordo}`, borderRadius: 8, background: '#fff', padding: '16px 12px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, cursor: 'pointer', fontFamily: 'inherit',
  },
  cardVeicoloAttivo: { borderColor: CONFIG.colori.trasporto, borderWidth: 2, background: 'rgba(235,28,36,0.05)' },
  cardVeicoloNome: { fontSize: 14, fontWeight: 700, letterSpacing: 0.3, textAlign: 'center' },
  cardVeicoloCarburante: { fontSize: 13, color: CONFIG.colori.testoSoft },
  cardVeicoloTag: { fontSize: 11, fontWeight: 700, color: '#fff', background: CONFIG.colori.dark, borderRadius: 4, padding: '3px 10px', marginTop: 3 },

  iconaTreno: { width: 'clamp(32px, 3vw, 46px)', height: 'clamp(32px, 3vw, 46px)', transform: 'rotate(-10deg)', marginTop: 26, flexShrink: 0 },
  risultatoTreno: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 'min(260px, 100%)', width: '100%', marginTop: 26 },
  risultatoTrenoHeader: {
    display: 'flex', gap: 20, fontSize: 13, fontWeight: 700, color: CONFIG.colori.dark,
    borderBottom: `1px solid ${CONFIG.colori.bordo}`, paddingBottom: 10, textAlign: 'center',
  },
  risultatoTrenoScroll: { maxHeight: 260, overflowY: 'auto', width: '100%' },
  risultatoTrenoRiga: {
    display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 20, padding: '16px 0 14px',
    borderBottom: `1px dashed ${CONFIG.colori.bordo}`, fontFamily: 'inherit',
    fontSize: 13, color: CONFIG.colori.dark, textAlign: 'center', width: '100%',
  },
  risultatoTrenoRigaAttiva: { background: 'rgba(235,28,36,0.05)' },
  rigaSpacerProsegui: { display: 'flex', alignItems: 'center', gap: 20, minWidth: 'min(260px, 100%)' },
  infoIcona: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24,
    borderRadius: '50%', border: `1px solid ${CONFIG.colori.testoSoft}`, fontSize: 13, fontStyle: 'italic',
    color: CONFIG.colori.testoSoft,
  },

  formVolo: { marginTop: 32, textAlign: 'center' },
  rigaTipoTratta: { display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 10, textAlign: 'left' },
  formVoloGrid: {
    display: 'grid', gridTemplateColumns: 'auto 1px 1fr', columnGap: 42, rowGap: 20,
    alignItems: 'start', textAlign: 'left',
  },
  formVoloGridMobile: { display: 'flex', flexDirection: 'column', gap: 26, textAlign: 'left', width: '100%' },
  divisoreVoloGrid: { width: 1, gridRow: '1 / 3', alignSelf: 'stretch', background: CONFIG.colori.bordo },
  rigaVolo: { display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 42, flexWrap: 'wrap' },
  rigaVoloGruppoSx: { display: 'flex', alignItems: 'flex-start', gap: 42, flexWrap: 'wrap', minWidth: 'min(610px, 100%)' },
  rigaVoloGruppoSxMobile: { display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 20, width: '100%' },
  campoVolo: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 'min(276px, 100%)', flex: '1 1 276px' },
  labelVolo: { fontSize: 'clamp(13px, 0.9vw, 16px)', color: CONFIG.colori.testoSoft },
  inputVoloWrap: {
    position: 'relative', display: 'flex', alignItems: 'center',
    borderBottom: `1px solid ${CONFIG.colori.bordo}`, minWidth: 'min(300px, 100%)', width: '100%',
  },
  inputVolo: {
    border: 'none', outline: 'none', background: 'transparent', fontSize: 'clamp(17px, 1.05vw, 21px)', fontFamily: 'inherit', textAlign: 'center',
    color: CONFIG.colori.dark, flex: 1, padding: '6px 28px 10px 0', width: '100%', boxSizing: 'border-box',
  },
  lenteVolo: { position: 'absolute', right: 0, pointerEvents: 'none' },
  sottoVolo: { fontSize: 14, color: CONFIG.colori.testoSoft, marginTop: 3 },
  rigaSottoA: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, flexWrap: 'nowrap', overflowX: 'auto', maxWidth: '100%', marginTop: 6 },
  pillTariffa: {
    border: `1px solid ${CONFIG.colori.trasporto}`, borderRadius: 20, background: '#fff',
    color: CONFIG.colori.trasporto, fontSize: 10, fontWeight: 700, padding: '3px 9px',
    cursor: 'pointer', fontFamily: 'inherit', letterSpacing: 0.3,
    appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
  },
  bottoneProsegui: {
    background: '#fff', color: CONFIG.colori.trasporto, border: `2px solid ${CONFIG.colori.trasporto}`,
    borderRadius: 6, padding: '8px 32px', fontSize: 16, fontWeight: 700, letterSpacing: 0.5,
    cursor: 'pointer', fontFamily: 'inherit', alignSelf: 'center', marginTop: 26,
  },
  bottoneProseguiPiccolo: {
    background: '#fff', color: CONFIG.colori.trasporto, border: `1px solid ${CONFIG.colori.trasporto}`,
    borderRadius: 4, padding: '6px 14px', fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  bottoneProseguiPiccoloAttivo: { background: CONFIG.colori.trasporto, color: '#fff' },

  grigliaTasselli: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14 },
  tassello: {
    border: 'none', borderRadius: 10, color: '#fff', padding: '26px 14px', cursor: 'pointer',
    display: 'flex', flexDirection: 'column', gap: 6, fontFamily: 'inherit',
  },
  tasselloLabel: { fontSize: 18, fontWeight: 700 },
  tasselloConteggio: { fontSize: 12, opacity: 0.85 },

  barraNotti: { display: 'flex', alignItems: 'center', gap: 10, margin: '22px 0 6px' },
  barraNottiLabel: { fontSize: 12, color: CONFIG.colori.testoSoft, minWidth: 90 },
  barraNottiTrack: { display: 'flex', gap: 3, flexWrap: 'wrap', flex: 1 },
  nottePallino: { width: 16, height: 8, borderRadius: 2, display: 'inline-block' },
  barraNottiConteggio: { fontSize: 12, fontWeight: 600 },

  grigliaIcone: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))', gap: 10 },
  iconaScelta: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '14px 6px',
    border: `2px solid ${CONFIG.colori.bordo}`, borderRadius: 8, background: '#fff',
    cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
  },
  iconaGrande: { fontSize: 24 },

  alloggioIconeRiga: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 24,
    flexWrap: 'nowrap', overflowX: 'auto', maxWidth: '100%', margin: '10px 0 26px',
  },
  alloggioIconaMini: {
    background: 'transparent', border: 'none', borderRadius: 12, padding: 10, outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flexShrink: 0,
  },
  alloggioIconaEmoji: {
    display: 'inline-block', width: 44, height: 44,
    WebkitMaskSize: 'contain', maskSize: 'contain',
    WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center', maskPosition: 'center',
    transition: 'background-color 0.15s',
  },
  alloggioIconaLabel: { fontSize: 14, color: CONFIG.colori.testoSoft, fontFamily: 'inherit' },
  elementoIconaImg: { width: 20, height: 20, objectFit: 'contain', filter: 'grayscale(100%)' },

  anteprimaPagina: { width: '100%', marginTop: 3 },
  anteprimaColonne: { display: 'flex', width: '100%', minHeight: 580, alignItems: 'stretch', gap: 3 },
  anteprimaColonna: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  anteprimaHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '14px 36px', color: '#fff', position: 'relative',
  },
  anteprimaHeaderIcona: { width: 22, height: 22, objectFit: 'contain', filter: 'brightness(0) invert(1)' },
  anteprimaHeaderTitolo: { fontWeight: 800, fontStyle: 'italic', letterSpacing: 0.5, fontSize: 16, textTransform: 'uppercase' },
  anteprimaChiudi: {
    position: 'absolute', top: 10, right: 10, width: 20, height: 20, borderRadius: '50%', border: 'none',
    background: 'rgba(255,255,255,0.3)', color: '#fff', fontSize: 12, cursor: 'pointer', lineHeight: 1,
  },
  // stesso padding e gap del corpo dei segmenti, così il + di ripristino sta sulla stessa riga degli altri
  anteprimaRipristino: { flex: 1, padding: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 },
  anteprimaContenuto: { display: 'flex', flexDirection: 'column', gap: 10 },
  anteprimaRipristinoPiu: {
    width: 60, height: 60, border: `2px solid ${CONFIG.colori.testoSoft}`, background: 'transparent',
    color: CONFIG.colori.testoSoft, fontSize: 28, cursor: 'pointer', borderRadius: 4, fontFamily: 'inherit',
  },
  anteprimaCorpo: {
    flex: 1, padding: 16, display: 'flex', flexDirection: 'column', gap: 10,
    alignItems: 'stretch', justifyContent: 'flex-start',
  },
  anteprimaPiu: {
    alignSelf: 'center', flexShrink: 0, width: 60, height: 60, border: '2px solid rgba(255,255,255,0.8)', background: 'transparent',
    color: 'rgba(255,255,255,0.9)', fontSize: 28, cursor: 'pointer', borderRadius: 4, fontFamily: 'inherit',
  },
  risultatoAlloggio: {
    border: '2px solid', background: '#fff', padding: 10, display: 'flex', flexDirection: 'column', gap: 8,
    maxWidth: 420, textAlign: 'left',
  },
  risultatoAlloggioNome: { fontSize: 18, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase' },
  risultatoAlloggioFoto: { height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  risultatoAlloggioBarra: { display: 'flex', alignItems: 'stretch', justifyContent: 'space-between', gap: 8 },
  risultatoAlloggioDate: { fontSize: 14, fontWeight: 600, color: CONFIG.colori.dark, alignSelf: 'center' },
  risultatoAlloggioSeleziona: {
    border: 'none', color: '#fff', fontWeight: 800, fontSize: 15, padding: '8px 22px', cursor: 'pointer',
    fontFamily: 'inherit', letterSpacing: 0.5,
  },
  risultatoAlloggioPrezzo: { textAlign: 'right', borderTop: `1px solid ${CONFIG.colori.bordo}`, paddingTop: 6 },

  // Card della panoramica (layout dalla tavola XD "Raggruppa 1708", testi ridotti per le colonne a 1/4)
  cardBottoni: { display: 'flex', gap: 5, justifySelf: 'end' },
  cardBottone: {
    background: 'transparent', border: '1px solid rgba(255,255,255,0.8)', borderRadius: 4, color: '#fff',
    fontSize: 9, padding: '0 4px', lineHeight: 1.5, cursor: 'pointer', fontFamily: 'inherit',
  },
  cardBottoniMini: { display: 'flex', flexDirection: 'column', gap: 3, flexShrink: 0 },
  cardBottoneMini: {
    background: 'transparent', border: '1px solid rgba(255,255,255,0.8)', borderRadius: 3, color: '#fff',
    fontSize: 7, padding: '0 6px', lineHeight: 1.5, cursor: 'pointer', fontFamily: 'inherit',
  },
  cardEtichetta: {
    justifySelf: 'start', border: '1px solid rgba(255,255,255,0.8)', borderRadius: 4, padding: '2px 6px',
    fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
  },
  cardLink: { color: '#fff', fontSize: 11, textDecoration: 'underline', alignSelf: 'flex-start' },

  cardTratta: { color: '#fff', margin: '0 -8px', paddingBottom: 8 },
  cardTrattaRiga1: { display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 6, padding: '0 8px 14px' },
  cardTrattaRiga2: {
    display: 'flex', alignItems: 'center', gap: 'clamp(4px, 0.6vw, 10px)', padding: '0 8px 8px',
    borderBottom: '1px solid #fff',
  },
  cardTrattaPunto: { display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 },
  cardTrattaOra: { fontSize: 17, lineHeight: 1.1 },
  cardTrattaCitta: { fontSize: 8, textTransform: 'uppercase', maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardTrattaCentro: { flex: 1, minWidth: 30, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 },
  cardTrattaMedio: { fontSize: 10, fontWeight: 500, whiteSpace: 'nowrap' },
  cardTrattaLinea: { width: '100%', borderTop: '1px solid rgba(255,255,255,0.8)' },
  cardTrattaBarra: { width: 1, height: 30, background: '#fff', transform: 'rotate(20deg)', flexShrink: 0 },
  cardTrattaPrezzo: { fontSize: 16, fontWeight: 800, whiteSpace: 'nowrap', flexShrink: 0 },
  cardTrattaDettagli: { padding: '8px 8px 4px', display: 'flex', flexDirection: 'column', gap: 4 },
  cardTrattaVettore: { alignSelf: 'flex-end', fontSize: 12, fontStyle: 'italic', fontWeight: 700 },
  cardTrattaPercorso: { display: 'flex', alignItems: 'center', gap: 10 },
  cardTrattaDurataLato: { fontSize: 11, fontStyle: 'italic', minWidth: 22 },
  cardTrattaTimeline: { display: 'flex', flexDirection: 'column', gap: 22, borderLeft: '1px solid rgba(255,255,255,0.8)', paddingLeft: 12 },
  cardTrattaTappa: { position: 'relative', fontSize: 11, display: 'flex', alignItems: 'center' },
  cardTrattaPallino: {
    position: 'absolute', left: -17, width: 9, height: 9, borderRadius: '50%', background: '#fff',
  },
  cardTrattaExtra: { textAlign: 'right', fontSize: 11, fontStyle: 'italic' },

  cardAlloggio: { color: '#fff', display: 'flex', flexDirection: 'column', gap: 6, paddingBottom: 10, borderBottom: '1px solid rgba(255,255,255,0.6)' },
  cardAlloggioRiga1: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  cardAlloggioNome: { fontSize: 14, fontWeight: 700, fontStyle: 'italic', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardAlloggioPrezzo: { textAlign: 'right', fontSize: 14, fontWeight: 700 },
  cardAlloggioDate: {
    display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 'clamp(10px, 2vw, 30px)', fontSize: 13,
    borderTop: '1px solid rgba(255,255,255,0.8)', borderBottom: '1px solid rgba(255,255,255,0.8)', padding: '4px 0',
  },
  cardAlloggioSlash: { width: 1, height: 20, background: '#fff', transform: 'rotate(20deg)' },
  cardAlloggioNotti: { textAlign: 'right', fontSize: 13 },
  cardAlloggioFoto: { width: 'calc(100% + 16px)', margin: '4px -8px 0', height: 220, objectFit: 'cover', display: 'block' },
  cardAlloggioServizi: { margin: 0, paddingLeft: 18, fontSize: 11, display: 'flex', flexDirection: 'column', gap: 3 },

  griglieMini: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 },
  cardMini: { color: '#fff', display: 'flex', flexDirection: 'column', minWidth: 0, border: '1px solid rgba(255,255,255,0.5)' },
  cardMiniTesta: { display: 'flex', justifyContent: 'space-between', gap: 4, padding: '4px 4px 6px' },
  cardMiniTitolo: { fontSize: 12, fontWeight: 700, lineHeight: 1.1, minWidth: 0, overflowWrap: 'anywhere' },
  cardMiniRiga: { textAlign: 'right', fontSize: 11, padding: '3px 4px', borderTop: '1px solid rgba(255,255,255,0.8)', minHeight: 14 },
  cardMiniDettagli: { fontSize: 11, padding: '6px 4px', borderTop: '1px solid rgba(255,255,255,0.8)', display: 'flex', flexDirection: 'column', gap: 6 },
  anteprimaFooter: {
    display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 14,
    padding: '16px 4px', borderTop: `1px solid ${CONFIG.colori.bordo}`,
  },
  anteprimaFooterLabel: { fontSize: 12, color: CONFIG.colori.testoSoft, fontWeight: 700 },
  anteprimaFooterTotale: { fontSize: 22, color: CONFIG.colori.dark },

  modaleOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.6)', zIndex: 300,
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto',
  },
  modaleBox: {
    position: 'relative', background: '#fff', borderRadius: 12, padding: '32px 28px',
    maxWidth: 1100, width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.25)', fontFamily: 'inherit',
  },
  modaleChiudi: {
    position: 'absolute', top: 14, right: 14, background: 'transparent', border: 'none',
    fontSize: 22, cursor: 'pointer', color: CONFIG.colori.testoSoft, lineHeight: 1,
  },
  modaleRigaSopra: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', margin: '18px 0' },
  modaleColonneScroll: { overflowX: 'auto', maxWidth: '100%' },
  modaleColonne: { display: 'grid', gridTemplateColumns: 'repeat(6, minmax(140px, 1fr))', gap: 24, alignItems: 'start' },
  modaleColonna: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 },
  modalePillTitolo: {
    border: `1px solid ${CONFIG.colori.bordo}`, borderRadius: 20, background: '#fff',
    color: CONFIG.colori.dark, fontSize: 14, fontWeight: 600, padding: '6px 14px',
    cursor: 'pointer', fontFamily: 'inherit', marginBottom: 6, alignSelf: 'flex-start',
  },
  modaleCheckRiga: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: CONFIG.colori.dark, cursor: 'pointer' },
  checkboxGrande: { width: 16, height: 16, flexShrink: 0 },
  inputPrezzoModale: {
    padding: '9px 2px', border: 'none', borderBottom: `1px solid ${CONFIG.colori.bordo}`,
    borderRadius: 0, background: 'transparent', fontSize: 16, fontFamily: 'inherit', width: 80, boxSizing: 'border-box',
  },
  inputAlloggioGrande: {
    padding: '10px 2px', border: 'none', borderBottom: `1px solid ${CONFIG.colori.bordo}`,
    borderRadius: 0, background: 'transparent', fontSize: 20, fontFamily: 'inherit',
    width: '100%', boxSizing: 'border-box',
  },
  labelCampoAlloggioGrande: { fontSize: 19, fontWeight: 600, color: CONFIG.colori.testoSoft },
  grigliaChip: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  chip: {
    padding: '7px 12px', border: '2px solid', borderRadius: 20, background: '#fff',
    fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
  },

  form: { border: '2px solid', borderRadius: 8, padding: 18, marginTop: 16, background: '#fff' },
  sottoForm: { borderTop: '1px dashed', paddingTop: 14, marginTop: 14 },
  riquadroStima: {
    background: CONFIG.colori.chiaro, borderRadius: 6, padding: 12, marginTop: 12, fontSize: 13,
  },
  suggerimento: {
    border: '2px dashed', borderRadius: 8, padding: 14, fontSize: 14,
    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14, background: '#fff',
  },

  listaElementi: { listStyle: 'none', padding: 0, margin: '0 0 8px' },
  elemento: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
    border: `1px solid ${CONFIG.colori.bordo}`, borderRadius: 6, marginBottom: 8, background: '#fff',
  },
  elementoIcona: { fontSize: 20 },
  elementoTesto: { display: 'flex', flexDirection: 'column', flex: 1, fontSize: 14 },
  elementoMeta: { fontSize: 12, color: CONFIG.colori.testoSoft },
  elementoCosto: { fontWeight: 700, fontSize: 14 },
  rimuovi: {
    background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: CONFIG.colori.testoSoft,
  },

  grigliaNotti: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  notteBottone: {
    padding: '6px 10px', border: `1px solid ${CONFIG.colori.bordo}`, borderRadius: 6,
    fontSize: 12, fontFamily: 'inherit',
  },

  tab: {
    padding: '5px 10px', border: '1px solid', borderColor: CONFIG.colori.bordo, borderRadius: 6,
    background: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', color: CONFIG.colori.dark,
  },
  oppure: { fontSize: 13, color: CONFIG.colori.testoSoft },

  rigaBottoni: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 18 },
  bottonePrimario: {
    background: CONFIG.colori.dark, color: '#fff', border: 'none', borderRadius: 6,
    padding: '11px 22px', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  },
  bottoneSecondario: {
    background: '#fff', color: CONFIG.colori.dark, border: `1px solid ${CONFIG.colori.bordo}`,
    borderRadius: 6, padding: '11px 18px', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
  },
  bottoneMini: {
    border: 'none', borderRadius: 6, color: '#fff', padding: '7px 14px',
    fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', marginTop: 8,
  },
  linkPiccolo: {
    background: 'none', border: 'none', color: CONFIG.colori.testoSoft, fontSize: 13,
    cursor: 'pointer', padding: 0, fontFamily: 'inherit', textDecoration: 'underline',
  },

  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 50,
  },
  modale: {
    background: '#fff', borderRadius: 10, padding: 26, maxWidth: 560, width: '100%',
  },
  listaErrori: { paddingLeft: 18, margin: '0 0 12px' },
  errore: { fontSize: 14, color: CONFIG.colori.trasporto, marginBottom: 6 },
  riquadroAiuto: {
    background: CONFIG.colori.chiaro, borderLeft: `4px solid ${CONFIG.colori.attivita}`,
    padding: 14, borderRadius: 6, marginBottom: 12,
  },
  rigaAiuto: { fontSize: 14, margin: '0 0 8px' },

  footer: {
    display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
    padding: '14px 28px', background: CONFIG.colori.dark, color: '#fff', fontSize: 11, letterSpacing: 0.5,
  },
  voceFooter: { cursor: 'pointer' },
};
